/* SESSION_HISTORY — every practice session you have finished, behind the "N sessions" tile in
 * My Progress (docs/project/session-history.md).
 *
 * ── One card, two depths ────────────────────────────────────────────────────────────────────
 * The card walks rather than stacks: the header carries a breadcrumb — Session history › Today ·
 * 08:31 AM — every crumb is a button back to that depth, ◂ steps back one, Esc steps back one and
 * closes at the top, and the card never moves or changes size while you walk it. Three stacked
 * overlays would have been the literal reading of the original ask and the wrong one — each new
 * backdrop dims the one behind it, and closing becomes a guessing game about which × belongs to
 * what.
 *
 * It walked THREE depths until 2026-08-13, the third being the session's words. A session screen
 * whose headline number was a door to another screen made the words feel like a footnote, and the
 * step cost a click to reach the thing most worth looking at. The words now live ON the session
 * screen, under its numbers, where the count above them can be read against the list itself.
 *
 * The one thing that DOES open as its own overlay is the rich dictionary entry a word click raises:
 * that is a different KIND of thing (a dictionary, not this history), it is the app's one existing
 * door for "a word, over a modal" — openTermRichPopup, the same one the garden vista uses — and
 * stacking it means one Esc returns to the session with the filter still typed.
 *
 * ── Where the numbers come from ─────────────────────────────────────────────────────────────
 * The ⊙ review button replays the LAST finish screen from bytes the browser kept
 * (last-finish-review.md), which is why it dies with the tab and knows only one session. This is
 * the other half: the same facts re-derived server-side from user_session + user_session_sense, so
 * they survive the tab, the machine and the year. `flawless` and the median answer speed are
 * computed there to the letter of the client's own definitions — see SessionDetail — because a
 * session must not report one number live and a different one a week later. Every number a row
 * shows is on the row itself, so the list and a session's own screen quote one rollup.
 *
 * The list is fetched once per opening; each session's words are fetched on first click and cached
 * for as long as the card is open, so walking back and forth costs nothing.
 */
window.SESSION_HISTORY = (function () {
	"use strict";

	var layer = null;
	var $overlay = null, $card = null, $crumbs = null, $body = null, $back = null;

	var stack = [];          // [{ title, render }] — depth 0 is the list
	var pair = { from: "", to: "" };
	var rowsData = null;     // the fetched session list
	var detailCache = {};    // sessionId → SessionDetail, for this opening only
	// sessionId → the /api/session-word-audit response, "notfound" (404) or "failed" — the
	// owner-only word-decision audit table (session-word-audit.md), same lifetime as detailCache.
	var auditCache = {};

	// The words door (openWords — the "words mastered" tile) shares this same card: same level
	// stack, same breadcrumbs, a different root. Both roots load the session list — the words
	// root draws its trend charts from it, and a word's screen links back into session details.
	var wordsData = null;    // the fetched word-stats list
	var wordHistCache = {};  // termId → WordHistoryDetail, for this opening only

	// Every table's ordering, keyed by table: id → { key, numeric, desc } (or null for "as it
	// arrived"). Module state rather than a local in the render function, because the body is
	// rebuilt from scratch on every step of the walk (paintLevel) — a local would be thrown away
	// the moment you opened a row and came back, which is exactly when you want the order kept.
	var sorts = {};

	// The app's "explain me only while I am cut off" marker (main.js CLIP_TIP_CLASS, one document
	// -wide PL.tip.delegate). Spelt as a literal here for the same reason the word-list rows spell
	// it: the class is the contract, and main.js's copy is closure-local.
	var CLIP_TIP = "plClipTipClass";

	// ── formatting ───────────────────────────────────────────────────────────────────────────
	// ⛔ This block said "Browser-formatted throughout, so dates and times follow the user's own
	// locale" until 2026-09-01. Owner ruling that day (docs/project/i18n-runtime-grammar.md §5.3):
	// THE APP LOCALE WINS. The pill the user set decides how a date is written, exactly as it
	// decides every other string on the page — and numbers already worked that way, so a Russian
	// UI on an en-US machine was showing «24 000 слов» beside «9/1/2026» in one panel.
	//
	// The old comment's fear — "a format we would have to translate five times" — was never the
	// alternative: localeTag() still hands the formatting to Intl, it only tells Intl WHICH locale.
	// login_main.js had already broken ranks for exactly this reason, with a bug behind it.

	function fmtTime(ms) {
		return new Date(ms).toLocaleTimeString(localeTag(), { hour: "2-digit", minute: "2-digit" });
	}

	// An ISO learning-day ("2026-08-11") as a readable date. Parsed at NOON local: a bare
	// "2026-08-11" is parsed as UTC midnight and reads as the 10th anywhere west of Greenwich.
	function fmtDay(iso) {
		if (!iso) { return ""; }
		var d = new Date(iso + "T12:00:00");
		return isNaN(d) ? iso
			: d.toLocaleDateString(localeTag(), { weekday: "short", month: "short", day: "numeric" });
	}

	function dayLabel(row) {
		if (row.daysAgo === 0) { return getRes("hist_today"); }
		if (row.daysAgo === 1) { return getRes("hist_yesterday"); }
		return fmtDay(row.dayIso);
	}

	/** The same day WITHOUT its weekday — for a row that has to carry its own date because the
	 *  day headers are gone (a sorted session list). A stamp in a narrow column beside eight
	 *  figures, not a heading: "Aug 9 · 08:31 AM" says everything "Sat, Aug 9" adds a word to. */
	function dayLabelShort(row) {
		if (row.daysAgo === 0) { return getRes("hist_today"); }
		if (row.daysAgo === 1) { return getRes("hist_yesterday"); }
		if (!row.dayIso) { return ""; }
		var d = new Date(row.dayIso + "T12:00:00");
		return isNaN(d) ? row.dayIso : d.toLocaleDateString(localeTag(), { month: "short", day: "numeric" });
	}

	// Session lengths, in the finish card's own units (main.js fmtSummaryDuration): seconds under a
	// minute, "6m 20s" above it. The My Progress "studied" tile rounds to whole minutes instead —
	// correct there, where the number is hours of a lifetime, and wrong here, where a 40-second
	// session must not read as "0m".
	function fmtDuration(sec) {
		if (sec < 60) { return Math.round(sec) + "s"; }
		return Math.floor(sec / 60) + "m " + Math.round(sec % 60) + "s";
	}

	/** The same duration as a CELL: minutes and seconds in their own right-aligned tracks, so
	 *  6m 3s, 19m 22s and 11s line up on both boundaries down a column. A session under a minute
	 *  leaves the minute track empty rather than printing "0m". */
	function durationCell(cssClass, sec) {
		var mins = sec < 60 ? 0 : Math.floor(sec / 60);
		var secs = sec < 60 ? Math.round(sec) : Math.round(sec % 60);
		return $("<span></span>").addClass(cssClass).addClass("histDurClass")
			.append($('<span class="histDurMinClass"></span>').text(mins > 0 ? mins + "m" : ""))
			.append($('<span class="histDurSecClass"></span>').text(secs + "s"));
	}

	/** Right ÷ shown. The denominator is spelt as right + revealed because those are the two ways a
	 *  showing can end, which is the same thing and says WHY it is the same thing. */
	function fmtAccuracy(row) {
		var total = row.totalCorrect + row.totalTimeouts;
		return total > 0 ? Math.round(row.totalCorrect / total * 100) + "%" : "—";
	}

	/** The finish screen's "typical answer": the median of the per-word averages, one decimal. */
	function fmtTypical(row) {
		return row.medianAnswerMs != null ? (row.medianAnswerMs / 1000).toFixed(1) + "s" : "—";
	}

	// Which way the drill asked, in that session's own letters. A Mix session has no single answer
	// (its direction is per word), and an audio cue is a different question again, so both get a
	// name rather than a pair of arrows that would be a lie.
	//
	// The gloss side comes off the ROW, not off `pair`. Since 2026-08-25 this list is scoped to the
	// LEARNED language and spans every gloss language
	// (docs/project/progress-keyed-on-learned-language.md), so a session drilled against English
	// hints sits above one drilled against Russian — and reading `pair.from` for both would print
	// today's gloss language onto yesterday's session, which is the same class of lie the two named
	// directions above exist to avoid. `pair.to` still serves the other side: every row in the list
	// has the same learned language by construction.
	//
	// The fallback covers two callers, and only one of them is a real gap: a session row written
	// before `lang_from` was populated (harmless — there are none in practice), and the owner-only
	// audit table's synthetic `{direction: …}` objects, which are keyed by sessionId alone and have
	// no gloss language to offer. That table can therefore still print today's gloss language onto
	// an older session's word. Left as is deliberately: it is a debugging surface behind
	// learn.audit.user-ids, and closing it means threading a gloss field through a third DTO.
	function glossOf(row) {
		return row.glossLang || pair.from;
	}

	function fmtDirection(row) {
		if (row.direction === "MIXED") { return getRes("hist_dir_mix"); }
		if (row.direction === "DICTATION") { return getRes("hist_dir_byEar"); }
		return row.direction === "TERM_TO_MEANING"
			? pair.to + " → " + glossOf(row)
			: glossOf(row) + " → " + pair.to;
	}

	// Where the words came from, or "" when the stored origin does not actually say.
	//
	// WORD_LIST with no word_list_id is the COMMON case, not an anomaly: the tray is filled from
	// the word-lists panel for virtual lists too (levels, verbs, topics, garden sections), and none
	// of those is a word_list row. On this user's data that is 80 of 81 sessions — so printing
	// "from a word list" for it would put a string that says nothing on almost every row. A source
	// is shown only when it names something.
	function fmtSource(row) {
		if (row.wordListName) { return strFormat(getRes("hist_origin_list"), row.wordListName); }
		if (row.origin === "SEARCH") { return getRes("hist_origin_search"); }
		if (row.origin === "RANDOM") { return getRes("hist_origin_random"); }
		return "";
	}

	// ── the card ─────────────────────────────────────────────────────────────────────────────

	function open(anchor) {
		openCard(anchor, { title: getRes("hist_title"), render: renderList }, loadList);
	}

	// The words root: every practiced word as one sortable table, the pair's trend charts above
	// it, each row a door into that word's own history.
	function openWords(anchor) {
		openCard(anchor, { title: getRes("hist_ws_title"), render: renderWordStats }, function () {
			loadList();
			loadWordStats();
		});
	}

	// Straight to ONE word's screen, from outside this file — the garden plant on a dictionary,
	// Learn or Practice row (garden.js's click delegate). It opens the card at the words root and
	// steps in, rather than pushing the word alone, so the ‹ crumb reads "Word statistics" and
	// walks out to every word the way it does when the My Progress tile is the door. One card,
	// one level stack, however it was entered.
	// With the card ALREADY open it is one more step of the same walk instead — a rich entry
	// raised over the card (the headword's door) carries a plant of its own, and a click there
	// that did nothing would be the one dead control on the screen.
	function openWord(termId, termText, anchor) {
		if (layer) { openWordLevel(termId, termText); return; }
		openWords(anchor);
		if (!layer) { return; }   // no language pair on the page — openCard declined to open
		openWordLevel(termId, termText);
	}

	// The accuracy root — the "accuracy" tile's own screen: how the pair's one percentage is
	// built and how it moves. Nothing here has a query of its own: the sessions carry the two
	// trends, the words carry the hardest-words diagnosis, and both lists are the ones the other
	// two doors already load.
	function openAccuracy(anchor) {
		openCard(anchor, { title: getRes("hist_acc_title"), render: renderAccuracy }, function () {
			loadList();
			loadWordStats();
		});
	}

	// The time root — the "studied" tile's own screen: where the hours went, day by day and in
	// total. The word list rides along for one number: distinct words mastered, the denominator
	// of "what an hour of practice buys".
	function openTime(anchor) {
		openCard(anchor, { title: getRes("hist_time_title"), render: renderTime }, function () {
			loadList();
			loadWordStats();
		});
	}

	// The owner-only word-decision audit table (session-word-audit.md) — the whole rationale
	// behind every per-word decision of ONE session, reachable from two places: the finish
	// screen's 🧪 pill (this card is not open yet, so this becomes the ROOT level, same as
	// open()/openWords() — no "Session history" list underneath, since the finish screen has one
	// specific session in mind and nothing to list) and the session-history row's audit chip
	// (the card is already open on the list, so this pushes onto its stack exactly the way
	// openDetail/openWordLevel push a session or a word onto it).
	function openAudit(sessionId, anchor) {
		var level = auditLevel(sessionId);
		if (layer) {
			pushLevel(level);
			loadAudit(sessionId);
			return;
		}
		openCard(anchor, level, function () { loadAudit(sessionId); });
	}

	function openCard(anchor, rootLevel, load) {
		if (layer) { return; }
		pair.from = $("#langFromValId").attr("data-lang-code") || "";
		pair.to = $("#langToValId").attr("data-lang-code") || "";
		if (!pair.from || !pair.to) { return; }

		detailCache = {};
		auditCache = {};
		rowsData = null;
		stack = [];
		wordsData = null;
		wordHistCache = {};
		sorts = {};

		$overlay = $('<div class="histOverlayClass"></div>');
		$card = $('<div class="histCardClass" role="dialog" aria-modal="true"></div>');
		$back = $('<button type="button" class="pl-btn-icon-round histBackClass"></button>')
			.html(plIcon("chevron-left"))
			.attr("aria-label", getRes("hist_back_label"))
			.on("click", function () { popLevel(); });
		$crumbs = $('<nav class="histCrumbsClass"></nav>');
		var $close = $('<button type="button" class="pl-btn-icon-round histCloseClass"></button>')
			.html(plIcon("x"))
			.attr("aria-label", getRes("tipClose_label"))
			.on("click", function () { close(); });
		$body = $('<div class="histBodyClass"></div>');

		$card.append($('<div class="histHeadClass"></div>').append($back).append($crumbs).append($close))
			.append($body);
		$overlay.append($card).appendTo(document.body);

		var focusToken = PL.focus.remember();
		var cardEl = $card[0];
		layer = PL.layers.push({
			el: cardEl,
			anchors: anchor ? [anchor] : [],
			// Esc walks the card back the way it was walked in, and only closes from the top. The
			// same order the crumbs read in, and the same order the rich popup underneath this one
			// unwinds its own drill-ins.
			onEsc: function () {
				if (stack.length > 1) { popLevel(); return true; }
				return false;
			},
			onClose: function () {
				layer = null;
				$overlay.remove();
				$overlay = $card = $crumbs = $body = $back = null;
				stack = [];
				detailCache = {};
				auditCache = {};
				wordsData = null;
				wordHistCache = {};
				sorts = {};
				// ifWithin the card we just removed: the tile that opened this gets focus back
				// only when the card was what held it, never when the user has moved on.
				if (focusToken) { focusToken.restore({ ifWithin: cardEl }); focusToken = null; }
			}
		});

		pushLevel(rootLevel);
		load();
	}

	function close() {
		if (layer) { layer.close("api"); }
	}

	// ── depth ────────────────────────────────────────────────────────────────────────────────

	function pushLevel(level) {
		stack.push(level);
		paintLevel();
	}

	function popLevel() {
		if (stack.length <= 1) { close(); return; }
		stack.pop();
		paintLevel();
	}

	function popTo(depth) {
		if (depth >= stack.length - 1) { return; }
		stack.length = depth + 1;
		paintLevel();
	}

	function paintLevel() {
		$back.css("display", stack.length > 1 ? "inline-flex" : "none");
		$crumbs.empty();
		for (var i = 0; i < stack.length; i++) {
			if (i) {
				$crumbs.append($('<span class="histCrumbSepClass" aria-hidden="true"></span>')
					.html(plIcon("chevron-right")));
			}
			var last = i === stack.length - 1;
			if (last) {
				$crumbs.append($('<span class="histCrumbNowClass"></span>').text(stack[i].title));
			} else {
				$crumbs.append($('<button type="button" class="histCrumbClass"></button>')
					.text(stack[i].title)
					.on("click", (function (depth) {
						return function () { popTo(depth); };
					})(i)));
			}
		}
		// The body is rebuilt from scratch on every step, INCLUDING a step back: nothing here is
		// expensive (the data is already in hand) and a kept-alive DOM would have to keep a filter
		// box, a scroll position and a pager honest across a walk. One rule, no bookkeeping.
		// histBodyDetailClass is the session screen's own layout (it stops scrolling and hands that
		// job to the word rows); it is dropped here so a step BACK to the list cannot inherit it.
		$body.removeClass("histBodyDetailClass").empty().scrollTop(0);
		stack[stack.length - 1].render($body);
	}

	function renderState(cssClass, text) {
		return $('<div class="histStateClass"></div>').addClass(cssClass).text(text);
	}

	// ── level 1: the sessions ────────────────────────────────────────────────────────────────

	function loadList() {
		var tz = "";
		try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch (e) { tz = ""; }
		$.ajax({
			url: "api/session-history",
			type: "GET",
			data: { langFrom: pair.from, langTo: pair.to, tz: tz },
			dataType: "json"
		}).done(function (data) {
			if (!layer) { return; }
			rowsData = (data && data.sessions) || [];
			if (stack.length === 1) { paintLevel(); }
		}).fail(function () {
			if (!layer) { return; }
			rowsData = "failed";
			if (stack.length === 1) { paintLevel(); }
		});
	}

	function renderList($into) {
		if (rowsData === null) {
			$into.append(renderState("", getRes("hist_loading")));
			return;
		}
		if (rowsData === "failed") {
			$into.append(renderState("histStateErrClass", getRes("hist_failed")));
			return;
		}
		if (!rowsData.length) {
			$into.append(renderState("", getRes("hist_empty")));
			return;
		}

		var $list = $('<div class="histListClass"></div>');
		// The list ARRIVES newest-first, so that is a real ordering and the arrow says so from the
		// first paint rather than after the first click.
		var sort = sortable({
			id: "sessions",
			value: sessionValue,
			tie: function (row) { return -row.startedAtMs; },
			initial: { key: "time", numeric: true, desc: true }
		});
		// The column names are PINNED, not scrolled away with the rows: past the first screenful a
		// column of bare figures whose headings have left is unreadable, and this list runs to
		// hundreds of rows. The day headers then park directly below them (see pinDayHeaders).
		var $legend = $('<div class="histLegendClass plStickyHeadClass"></div>')
			.append(sort.head("hist_col_time", "hist_tip_time", "time", true))
			.append(sort.head("hist_col_mastered", "hist_tip_mastered", "mastered", true))
			.append(sort.head("hist_col_flawless", "hist_tip_flawless", "flawless", true))
			.append(sort.head("hist_col_accuracy", "hist_tip_accuracy", "accuracy", true))
			.append(sort.head("hist_col_shown", "hist_tip_shown", "shown", true))
			.append(sort.head("hist_col_wrong", "hist_tip_wrong", "wrong", true))
			.append(sort.head("hist_col_spent", "hist_tip_spent", "spent", true))
			.append(sort.head("hist_col_typical", "hist_tip_typical", "typical", true))
			// The last column is the drill's DIRECTION, plus a source when there is one to name —
			// so it wears the same word the session's own fact table uses for it, not a second one.
			.append(sort.head("hist_fact_direction", "hist_tip_direction", "direction", false));

		// Day headers group rows that are NEXT TO each other in time — which they are only while
		// TIME is what orders the table. Sorted by any other column the rows come from all over
		// the history, so the headers would be lies: they go, and each row says its own day in the
		// first column instead (histFlatClass widens that track for it). Sorting BY time, either
		// direction, keeps them — the groups just run the other way.
		function repaint() {
			var s = sort.state();
			var byDay = !s || s.key === "time";
			// detach, not empty-and-rebuild: jQuery's .empty() strips its children's handlers, and
			// the legend's own click handlers are what got us here. Everything else in the list is
			// rebuilt from `rowsData` anyway, so only the legend has to survive the wipe.
			$legend.detach();
			$list.toggleClass("histFlatClass", !byDay).empty().append($legend);
			var rows = sort.order(rowsData);
			var lastDay = null;
			for (var i = 0; i < rows.length; i++) {
				if (byDay && rows[i].dayIso !== lastDay) {
					lastDay = rows[i].dayIso;
					// plStickyHeadClass is the contract with the scroll pager: a header parked at
					// the surface's top edge owns that strip, and the pager's up bar starts below it.
					$list.append($('<div class="histDayClass plStickyHeadClass"></div>')
						.text(dayLabel(rows[i])));
				}
				$list.append(sessionRow(rows[i], !byDay));
			}
			sort.mark($legend);
			pinDayHeaders($list, $legend);
		}
		sort.repaint = repaint;
		repaint();

		$into.append($list);
		// Long histories are exactly the surface the pager exists for (scroll-pager.md).
		if (window.SCROLL_PAGER) { SCROLL_PAGER.attach($into[0]); }
	}

	// One ordering rule per column of the session list. The two "n of N" columns sort on the
	// count, not the ratio: the big number in them is what the column is read for, and the
	// denominator is right there beside it. Accuracy is the only ratio, and it is a ratio in the
	// cell too. A session that answered nothing has no accuracy and no typical answer — null, so
	// it sinks rather than claiming a perfect or a hopeless score it never earned.
	function sessionValue(row, key) {
		switch (key) {
			case "time": return row.startedAtMs;
			case "mastered": return row.termsMastered;
			case "flawless": return row.flawless;
			case "accuracy": {
				var total = row.totalCorrect + row.totalTimeouts;
				return total > 0 ? row.totalCorrect / total : null;
			}
			case "shown": return row.presentations;
			case "wrong": return row.totalTimeouts;
			case "spent": return row.durationSec;
			case "typical": return row.medianAnswerMs;
			// What the last column READS as, source and all — so the eye's grouping and the sort's
			// grouping are the same grouping.
			case "direction": return fmtDirection(row) + " " + fmtSource(row);
		}
		return null;
	}

	// Two things stick to the same top edge, and the lower one has to know how tall the upper one
	// is. Measured rather than hard-coded: the legend's height follows the card's own font size and
	// the browser's minimum-font setting, and a guess that is 2px short lets row text show through
	// the seam on every scroll.
	function pinDayHeaders($list, $legend) {
		var h = $legend[0].getBoundingClientRect().height;
		if (h) { $list[0].style.setProperty("--histLegendH", h + "px"); }
	}

	/** A count over the set it is out of — "12 of 15". The denominator IS the information here:
	 *  without it a short session and a session that went badly print the same number. Three
	 *  tracks, not one string: the count, the word and the total each get their own, so "1 of 5"
	 *  and "13 of 18" line up on both numbers down a column. */
	function outOfCell(cssClass, count, total) {
		return $("<span></span>").addClass(cssClass).addClass("histOutOfClass")
			.append($('<b></b>').text(count))
			.append($('<span class="histOfClass"></span>').text(getRes("hist_of_label")))
			.append($('<span class="histOfTotalClass"></span>').text(total));
	}

	function numCell(cssClass, value) {
		return $("<span></span>").addClass(cssClass).text(value);
	}

	// ── column headings, and sorting by them ─────────────────────────────────────────────────
	// Every table on this card is a page of figures somebody opened to find an extreme in: the
	// word that will not stick, the evening that went badly, the session where a word finally
	// clicked. Reading a column top to bottom answers that only when the column is ordered, so
	// every heading on every table sorts — one helper, so a column cannot behave one way here
	// and another way one level down.
	//
	// The words root did this alone until 2026-08-14; this is that code, generalized, with its
	// rules intact: numeric columns open DESC (a table of figures is opened to find the biggest),
	// text opens A→Z, a second click flips, and "no data" sinks to the end in BOTH directions —
	// an em dash must never win a sort at either end.
	//
	// A heading also says what it COUNTS: every figure here is a rule ("right ÷ shown", "the
	// median of the per-word averages") that a one-word heading cannot carry, and the alternative
	// — a paragraph under the table naming ten columns — is a paragraph nobody reads to understand
	// one number. The tip is the app's own hover engine (PL.tip via assignTooltip), so these
	// behave exactly like every other explained control.

	/**
	 * One table's sorting.
	 *
	 * @param opts.id       slot in `sorts` — the ordering survives a walk into a row and back
	 * @param opts.value    (item, key) → that column's value for that row, null for "no data"
	 * @param opts.tie      (item) → a stable tie-break the eye can follow (a word, a date); a
	 *                      number sorts as a number, anything else by locale
	 * @param opts.initial  the order the rows are BUILT in, so the arrow tells the truth before
	 *                      anything has been clicked (omit when that order is the server's)
	 */
	function sortable(opts) {
		if (!(opts.id in sorts)) { sorts[opts.id] = opts.initial || null; }

		function tie(a, b) {
			var ta = opts.tie(a), tb = opts.tie(b);
			return typeof ta === "number" ? ta - tb : String(ta).localeCompare(String(tb));
		}

		function comparator(s) {
			return function (a, b) {
				var va = opts.value(a, s.key), vb = opts.value(b, s.key);
				if (va == null && vb == null) { return tie(a, b); }
				if (va == null) { return 1; }
				if (vb == null) { return -1; }
				var c = s.numeric ? (va - vb) : String(va).localeCompare(String(vb));
				if (s.desc) { c = -c; }
				return c !== 0 ? c : tie(a, b);
			};
		}

		var api = {
			/** Assigned by the caller once it has one — a click has to redraw something. */
			repaint: function () {},

			state: function () { return sorts[opts.id]; },

			/** A column heading that sorts — the sortable twin of headCell. */
			head: function (labelKey, tipKey, sortKey, numeric) {
				function toggle() {
					var s = sorts[opts.id];
					sorts[opts.id] = (s && s.key === sortKey)
						? { key: sortKey, numeric: numeric, desc: !s.desc }
						: { key: sortKey, numeric: numeric, desc: !!numeric };
					api.repaint();
				}
				// The label lives in its own span so that IT is what gets ellipsised in a narrow
				// column — the arrow rides on the heading itself and must always be readable, or
				// the table's ordering becomes something you can only find out by clicking.
				return $("<span></span>").assignTooltip(tipKey)
					.append($('<span class="histSortLblClass"></span>').text(getRes(labelKey)))
					.addClass("histSortHeadClass").attr("data-sort", sortKey)
					.attr("role", "button").attr("tabindex", 0)
					.on("click", function () { toggle(); })
					.on("keydown", function (e) {
						if (e.which === PL.keys.ENTER || e.which === PL.keys.SPACE) {
							toggle();
							e.preventDefault();
							// SPACE is the sound key everywhere else on this app; a heading being
							// operated must not also pronounce whatever is behind the card.
							e.stopPropagation();
						}
					});
			},

			/** The items in the table's current order — a copy, so the caller keeps its own. */
			order: function (items) {
				var s = sorts[opts.id];
				return s ? items.slice().sort(comparator(s)) : items.slice();
			},

			/** The arrow goes on the column that is actually ordering the rows, and nowhere else.
			 *  aria-sort says the same thing to a screen reader, which cannot see a ::after ↑. */
			mark: function ($legend) {
				var s = sorts[opts.id];
				$legend.children().removeClass("histSortAscClass histSortDescClass")
					.removeAttr("aria-sort");
				if (s) {
					$legend.children("[data-sort='" + s.key + "']")
						.addClass(s.desc ? "histSortDescClass" : "histSortAscClass")
						.attr("aria-sort", s.desc ? "descending" : "ascending");
				}
			}
		};
		return api;
	}

	function sessionRow(row, withDay) {
		var $btn = $('<button type="button" class="histRowClass"></button>')
			.toggleClass("histRowStoppedClass", row.outcome === "STOPPED");
		// "08:31 AM" under a day header, "Aug 9 · 08:31 AM" without one — the same shape the
		// session's own breadcrumb wears, so a row and the screen it opens name themselves alike.
		$btn.append($('<span class="histCellTimeClass"></span>')
			.text(withDay ? dayLabelShort(row) + " · " + fmtTime(row.startedAtMs)
				: fmtTime(row.startedAtMs)));
		$btn.append(outOfCell("histCellWordsClass", row.termsMastered, row.termCount));
		// Flawless is out of the words actually DRILLED, not the words planned — the same
		// denominator the session's own tile shows, and the one the ✓ marks are counted over.
		$btn.append(outOfCell("histCellFlawlessClass", row.flawless, row.wordCount));
		$btn.append(numCell("histCellAccClass", fmtAccuracy(row)));
		$btn.append(numCell("histCellShownClass", row.presentations));
		// WRONG is the number of showings that ended with the answer being revealed — the timer
		// expiring, or the learner asking to see it. It is not "wrong Enter presses": those are
		// retries INSIDE a showing that the showing survives, and how many times somebody hit Enter
		// is not a fact about their learning. With this reading shown = right + wrong.
		$btn.append(nilCell("histCellWrongClass", row.totalTimeouts));
		$btn.append(durationCell("histCellSpentClass", row.durationSec));
		$btn.append(numCell("histCellTypicalClass", fmtTypical(row)));
		var $set = $('<span class="histCellSetClass"></span>')
			.append($('<span class="histDirClass"></span>').text(fmtDirection(row)));
		if (fmtSource(row)) {
			$set.append($('<span class="histSrcClass"></span>').text(fmtSource(row)));
		}
		if (row.outcome === "STOPPED") {
			$set.append($('<span class="histStoppedChipClass"></span>').text(getRes("hist_stopped")));
		}
		// Owner-only word-decision audit (session-word-audit.md): a quiet door straight to the
		// rationale table for THIS session, only where the server says rows exist for the
		// viewer — absent for everyone else. A span, not a button: the row itself is already one,
		// and a nested button is invalid HTML — stopPropagation keeps its click from also opening
		// the row's own session-detail level.
		if (row.hasAudit) {
			var $audit = $('<span class="histAuditChipClass" role="button" tabindex="0"></span>')
				.text(getRes("audit_history_chip"))
				.assignTooltip("audit_finish_tooltip")
				.on("click", function (e) {
					e.stopPropagation();
					openAudit(row.id, this);
				})
				.on("keydown", function (e) {
					if (e.which === PL.keys.ENTER || e.which === PL.keys.SPACE) {
						e.preventDefault();
						e.stopPropagation();
						openAudit(row.id, this);
					}
				});
			$set.append($audit);
		}
		$btn.append($set);
		// No chevron: the whole row is the door and says so by lighting up under the pointer. One
		// arrow per row, on a ten-column table, read as a column of controls rather than as a hint.
		$btn.on("click", function () { openDetail(row); });
		return $btn;
	}

	// ── level 2: one session, and its words ──────────────────────────────────────────────────

	function openDetail(row) {
		var title = dayLabel(row) + " · " + fmtTime(row.startedAtMs);
		pushLevel({
			title: title,
			render: function ($into) { renderDetail($into, row); }
		});
		if (detailCache[row.id]) { return; }

		$.ajax({
			url: "api/session-detail",
			type: "GET",
			data: { sessionId: row.id },
			dataType: "json"
		}).done(function (data) {
			if (!layer) { return; }
			detailCache[row.id] = data || "failed";
			repaintIfShowing(title);
		}).fail(function () {
			if (!layer) { return; }
			detailCache[row.id] = "failed";
			repaintIfShowing(title);
		});
	}

	// A response that lands after the user has already walked on must not redraw whatever they are
	// looking at now. The title is the level's identity here — it carries the session's own
	// timestamp, so it names exactly one row.
	function repaintIfShowing(title) {
		if (stack.length && stack[stack.length - 1].title === title) { paintLevel(); }
	}

	function statTile(value, label, tipKey) {
		return $('<div class="stats-cell"></div>')
			.append($('<span class="stats-num"></span>').text(value))
			.append($('<span class="stats-lbl"></span>').text(label))
			.assignTooltip(tipKey);
	}

	/** A label/value pair from the fact strip, explained on hover like every column heading. */
	function factCell(labelKey, value, tipKey) {
		return $('<div class="histFactClass"></div>')
			.append($('<span class="histFactLblClass"></span>').text(getRes(labelKey)))
			.append($('<span class="histFactValClass"></span>').text(value))
			.assignTooltip(tipKey);
	}

	/**
	 * The article deficit (functional-verdict.md P5), or nothing at all.
	 *
	 * Under label rule v3 a wrong article on a correctly produced noun is a PASS — the word came
	 * back, its gender did not — so it has stopped appearing in any counter on this card. This is
	 * the one place it does appear, which is the whole reason the ledger is written. It states the
	 * right article first and the wrong ones after it, because the thing worth carrying away is
	 * `der`, not the mistake.
	 */
	function articleChip(d) {
		if (!d.articleMisses || !d.articleMisses.length) { return null; }
		// Newest first from the server, so the head of the list is the current expectation — and
		// the counts collapse a repeated slip into one entry ("die ×3" rather than three chips).
		var counts = {};
		var order = [];
		for (var i = 0; i < d.articleMisses.length; i++) {
			var typed = d.articleMisses[i].submitted || getRes("hist_wh_articleNone");
			if (counts[typed] == null) { counts[typed] = 0; order.push(typed); }
			counts[typed]++;
		}
		var $chip = $('<div class="histWhArticleClass"></div>')
			.append($('<span class="histWhArticleLblClass"></span>').text(getRes("hist_wh_article")))
			.append($('<span class="histWhArticleRightClass"></span>')
				.text(d.articleMisses[0].expected))
			.assignTooltip("hist_wh_tip_article");
		for (var k = 0; k < order.length; k++) {
			$chip.append($('<span class="histWhArticleWrongClass"></span>')
				.text(counts[order[k]] > 1 ? order[k] + " ×" + counts[order[k]] : order[k]));
		}
		return $chip;
	}

	function renderDetail($into, row) {
		var detail = detailCache[row.id];
		if (!detail) {
			$into.append(renderState("", getRes("hist_loading")));
			return;
		}
		if (detail === "failed") {
			$into.append(renderState("histStateErrClass", getRes("hist_failed")));
			return;
		}

		// The four tiles the finish screen opens on, in the same order and wearing the same
		// .stats-cell face — this IS that screen, told about a session that is over. None of them
		// is a door any more: what the words tile used to open is now the bottom half of this page.
		var $tiles = $('<div class="histTilesClass"></div>');
		$tiles.append(statTile(row.termsMastered, getRes("stats_label_mastered"),
			"hist_tip_mastered"));
		$tiles.append(statTile(row.flawless + " / " + row.wordCount, getRes("exSummary_flawless"),
			"hist_tip_flawless"));
		$tiles.append(statTile(fmtDuration(row.durationSec), getRes("exSummary_time"),
			"hist_tip_spent"));
		if (row.medianAnswerMs != null) {
			$tiles.append(statTile(fmtTypical(row), getRes("exSummary_speed"), "hist_tip_typical"));
		}
		$into.append($tiles);

		// Everything else as label/value pairs flowing across the card's width rather than down a
		// single column: with the words below them these facts are a reference strip, and nine of
		// them stacked pushed the first word off the screen. Deliberately flat and unranked — a
		// session that is over has no ask in it, so there is nothing here to lead the eye to.
		var $facts = $('<div class="histFactsClass"></div>');
		var when = fmtDay(row.dayIso) + " · " + fmtTime(row.startedAtMs)
			+ (row.endedAtMs ? " – " + fmtTime(row.endedAtMs) : "");
		$facts.append(factCell("hist_fact_when", when, "hist_tip_when"));
		$facts.append(factCell("hist_fact_direction", fmtDirection(row), "hist_tip_direction"));
		if (fmtSource(row)) {
			$facts.append(factCell("hist_fact_source", fmtSource(row), "hist_tip_source"));
		}
		$facts.append(factCell("hist_fact_set", row.termCount, "hist_tip_set"));
		// The same three numbers the word rows below carry, for the whole session — and in the same
		// order, so the strip reads as the column head of the table under it. Shown = right + wrong
		// once a session has run to the end; "wrong attempts" (Enter pressed on a wrong answer) used
		// to sit here as a fourth and broke that arithmetic for everyone who tried to add them up.
		$facts.append(factCell("hist_fact_shown", row.presentations, "hist_tip_shown"));
		$facts.append(factCell("hist_fact_correct", row.totalCorrect, "hist_tip_right"));
		$facts.append(factCell("hist_fact_wrong", row.totalTimeouts, "hist_tip_wrong"));
		if (detail.soundReplays > 0) {
			$facts.append(factCell("hist_fact_replays", detail.soundReplays, "hist_tip_replays"));
		}
		$facts.append(factCell("hist_fact_outcome",
			getRes(row.outcome === "STOPPED" ? "hist_outcome_stopped" : "hist_outcome_completed"),
			"hist_tip_outcome"));
		$into.append($facts);

		// The tiles, the strip and the table's own head STAY; only the word rows move. A session's
		// headline numbers are what the rows are read against, and scrolling them away to reach the
		// twentieth word means scrolling back up to remember what it is being compared to. The body
		// stops being the scroller for this level (histBodyDetailClass) and the rows become one.
		$into.addClass("histBodyDetailClass");
		renderWords($into, detail);
	}

	// ── the words, under the numbers they explain ────────────────────────────────────────────
	// Alphabetical (collated server-side in the learned language), mastered first and everything
	// else drilled under it. Showing only the mastered ones would leave the "12 of 15" above
	// unexplainable — the three missing words are precisely what a learner opens a session to find.
	//
	// ONE word per row, its counters in columns beside it. This was a five-across grid of chips
	// whose numbers lived in a hover title: a word's whole story was then reachable only by holding
	// a mouse still over it — not at all on a touch screen, one word at a time at best, and never
	// two words side by side. The same table the session list is, one level down and about words.

	function renderWords($into, detail) {
		var mastered = [], also = [];
		for (var i = 0; i < detail.words.length; i++) {
			(detail.words[i].mastered ? mastered : also).push(detail.words[i]);
		}
		if (!mastered.length && !also.length) {
			$into.append(renderState("", getRes("hist_words_none")));
			return;
		}

		// The replays column earns its width only in a session that HAD audio: in a typed drill every
		// row would read 0, and a column of zeros is the one thing worse than a missing column. Same
		// test the fact strip above already applies to the session's own replay count.
		var showReplays = detail.soundReplays > 0;
		var $sec = $('<div class="histWordsSecClass"></div>')
			.toggleClass("histHasReplaysClass", showReplays);
		var $bands = $('<div class="histWordsClass"></div>');
		var $empty = renderState("histNoMatchClass", getRes("hist_words_noMatch")).hide();

		// Sorting happens INSIDE each band, never across them: the split is mastered vs not, which
		// is the session's own headline number ("12 of 15") told as two lists, and an ordering by
		// answer time must not dissolve it. Each band answers "and which of THESE was slowest".
		var sort = sortable({
			id: "detailWords",
			value: function (r, key) { return wordValue(r.word, key); },
			tie: function (r) { return r.word.term; }
		});

		var bands = [];
		if (mastered.length) {
			bands.push(band("hist_words_masteredHead", mastered, false, showReplays));
		}
		if (also.length) {
			bands.push(band("hist_words_alsoHead", also, true, showReplays));
		}
		for (var b = 0; b < bands.length; b++) { $bands.append(bands[b].el); }

		var $legend = wordsLegend(showReplays, sort);
		// Reordering the row NODES rather than repainting the level: a repaint would throw away
		// the typed filter, and the rows already carry their own show/hide.
		sort.repaint = function () {
			for (var i = 0; i < bands.length; i++) { bands[i].order(sort); }
			sort.mark($legend);
		};
		sort.repaint();

		// The same box the word lists carry — same classes, same clear ×, and (via WORDFILTER) the
		// same substitutions, so `ueber` finds *über* here exactly as it does there. No debounce:
		// this filters an array already in hand, so every keystroke can afford to be answered.
		var $tools = $('<div class="histFilterRowClass"></div>')
			.append(filterBox(function (typed) {
				var hits = 0;
				for (var i = 0; i < bands.length; i++) { hits += bands[i].apply(typed); }
				$empty.toggle(hits === 0);
				// Column headings over nothing name columns that are not there — they leave with
				// the last row and come back with it.
				$legend.toggle(hits > 0);
			}));
		// What the two marks in the last columns mean, said once where the marks are. They are the
		// only things on this screen that are not numbers, and a mark nobody can name is decoration.
		$tools.append($('<span class="histCheckKeyClass"></span>')
			.append($('<span class="histMarkClass" aria-hidden="true"></span>').html(plIcon("star")))
			.append($("<span></span>").text(getRes("hist_firstTry_legend"))));
		$tools.append($('<span class="histCheckKeyClass"></span>')
			.append($('<span class="histFlawlessClass" aria-hidden="true"></span>').html(plIcon("check")))
			.append($("<span></span>").text(getRes("hist_flawless_legend"))));

		// The filter and the ✓ key sit ABOVE the scrolling surface; the column headings sit INSIDE
		// it, pinned to its top edge. That split is not cosmetic: a scrolling box takes its
		// scrollbar's width out of its own content, so headings placed outside it are ~15px wider
		// than the rows they name and every column drifts. Inside, they measure the same box.
		$sec.append($('<div class="histWordsHeadClass"></div>').append($tools))
			.append($bands.prepend($legend)).append($empty);
		$into.append($sec);
		// The pager belongs to the surface that actually scrolls — here that is the rows, not the
		// body (scroll-pager.md). Nothing above them moves, so it needs no sticky-head inset.
		if (window.SCROLL_PAGER) { SCROLL_PAGER.attach($bands[0]); }
	}

	// One template, two users — this legend and every word row (history.css declares it once, so a
	// column cannot be added to the rows and forgotten here). The counter columns borrow the session
	// list's own headings: the same number must not be called "Cards" one level up and "Shown" here.
	function wordsLegend(showReplays, sort) {
		var $el = $('<div class="histWLegendClass"></div>')
			.append(sort.head("hist_wcol_word", "hist_tip_word", "word", false))
			.append(sort.head("hist_wcol_meaning", "hist_tip_meaning", "meaning", false))
			.append(sort.head("hist_col_shown", "hist_tip_wshown", "shown", true))
			.append(sort.head("hist_wcol_right", "hist_tip_wright", "right", true))
			.append(sort.head("hist_col_wrong", "hist_tip_wwrong", "wrong", true));
		if (showReplays) {
			$el.append(sort.head("hist_wcol_replays", "hist_tip_replays", "replays", true));
		}
		return $el
			.append(sort.head("hist_col_accuracy", "hist_tip_accuracy", "accuracy", true))
			.append(sort.head("hist_col_typical", "hist_tip_wtypical", "typical", true))
			// The two verdict columns, in the order they happen: known on sight, then never fumbled
			// at all. A word can be the first without being the second, which is the point of both.
			.append(sort.head("hist_wcol_firstTry", "hist_tip_firstTry", "firstTry", true))
			.append(sort.head("hist_col_flawless", "hist_tip_wflawless", "flawless", true))
			// …and then what the APP did about it, which is a different kind of fact and sits after
			// everything the learner did. Appended last on purpose: the narrow-window rules hide
			// existing headings by index, and a column inserted anywhere else would shift all three
			// hide-lists at once.
			.append(sort.head("hist_wcol_growth", "hist_tip_growth", "growth", true))
			.append(sort.head("hist_wcol_next", "hist_tip_next", "next", true))
			.append(sort.head("hist_wcol_verdict", "hist_tip_verdict", "verdict", true));
	}

	// One ordering rule per column of a session's word table. The two marks sort as the yes/no
	// they are — first click brings the ★ words (or the ✓ ones) to the top, which is the only
	// question a column of marks can be asked.
	function wordValue(word, key) {
		switch (key) {
			case "word": return word.term;
			case "meaning": return word.meaning || "";
			case "shown": return word.presented;
			case "right": return word.correct;
			case "wrong": return word.timeouts;
			case "replays": return word.replays;
			// The cell's own rule, to the letter — right ÷ (right + revealed), not right ÷ shown:
			// the two agree, and quoting the arithmetic the cell prints keeps them agreeing.
			case "accuracy": {
				var total = word.correct + word.timeouts;
				return total > 0 ? word.correct / total : null;
			}
			case "typical": return word.avgMs;
			case "firstTry": return word.firstTry ? 1 : 0;
			case "flawless": return word.flawless ? 1 : 0;
			case "growth": return growthValue(word.stageBefore, word.stageAfter);
			case "next": return word.intervalDays;
			// Sorted by the finish screen's own reading order, not alphabetically: "recalled cold"
			// and "needed the answer shown" belong at opposite ends of this column, and no alphabet
			// puts them there.
			case "verdict": return HIST_VERDICT[word.verdict] ? HIST_VERDICT[word.verdict].rank : null;
		}
		return null;
	}

	function band(headKey, words, subdued, showReplays) {
		var $head = $('<div class="histBandHeadClass"></div>');
		var $rows = $('<div class="histBandRowsClass"></div>').toggleClass("histBandSubduedClass", !!subdued);
		var rows = [];
		for (var i = 0; i < words.length; i++) {
			rows.push(wordRow(words[i], showReplays));
			$rows.append(rows[i].el);
		}
		// "Mastered (39)" while everything is shown, "Mastered (1 of 39)" once a filter is on. The
		// heading is the only place the two numbers can be told apart — without the denominator a
		// filtered band claims a count its own rows contradict.
		function setHead(shown) {
			$head.text(strFormat(getRes(headKey), shown === words.length
				? fmtNum(words.length)
				: fmtNum(shown) + " " + strFormat(getRes("hist_of"), fmtNum(words.length))));
		}
		setHead(words.length);
		var $el = $('<div class="histBandClass"></div>').append($head).append($rows);
		return {
			el: $el,
			/** Re-append this band's rows in the table's order — the nodes move, so the typed
			 *  filter and each row's own show/hide state ride along with them. */
			order: function (sort) {
				var sorted = sort.order(rows);
				for (var j = 0; j < sorted.length; j++) { $rows.append(sorted[j].el); }
			},
			/** Repaint every row against the typed filter; returns how many still show. */
			apply: function (typed) {
				var shown = 0;
				for (var j = 0; j < rows.length; j++) {
					shown += rows[j].apply(typed) ? 1 : 0;
				}
				setHead(shown);
				$el.toggle(shown > 0);
				return shown;
			}
		};
	}

	/** A counter whose zero is the good news — dimmed, so the eye lands on the rows that are not. */
	function nilCell(cssClass, value) {
		return numCell(cssClass, value).toggleClass("histNilClass", !value);
	}

	// ── the scheduling story: growth · next · verdict ─────────────────────────────────────────
	// Three columns on BOTH word tables (a session's words, and a word's sessions), because they
	// answer the one question the counters cannot: what the app did with the word afterwards. Every
	// one of them was already on the finish screen and nowhere else — the growth strip's stage step,
	// its "next watering in N days", and the §9.1 line under the words — so a learner could read
	// them for ten seconds after a drill and never again.
	//
	// `rank` is the finish screen's own reading order (main.js EX_OUTCOME_ORDER — good news first,
	// the voids last), so sorting this column groups the session the way that screen groups it.
	//
	// The label is short because it lives in a column. The TOOLTIP used to be the finish screen's
	// own exOutcome_* sentence, reused verbatim — which for the first row meant a chip reading
	// "recalled cold" explained by a box reading "recalled cold" (owner report 2026-08-15), and
	// for the rest meant a rephrasing rather than an explanation. These are the app's own model
	// vocabulary; nothing outside this app teaches what "recalled cold" or "warm-up" means, so a
	// hover over one has exactly one job: say what the app saw and what it did about it. Hence
	// hist_vtip_*, written for a learner who has never read the SRS spec — the rules they
	// paraphrase are SrsFinishService's class list.
	var HIST_VERDICT = {
		RECALLED_COLD:  { res: "hist_v_cold",     tip: "hist_vtip_cold",     cls: "histVGoodClass",  rank: 1 },
		KNOWN_SLOW:     { res: "hist_v_slow",     tip: "hist_vtip_slow",     cls: "histVOkClass",    rank: 2 },
		KNOWN_ARTICLE:  { res: "hist_v_article",  tip: "hist_vtip_article",  cls: "histVOkClass",    rank: 3 },
		WARM_UP:        { res: "hist_v_warmUp",   tip: "hist_vtip_warmUp",   cls: "histVOkClass",    rank: 4 },
		// "answer shown" until 2026-08-25 — the one row that named what the DRILL did rather than
		// what the app concluded, which is the axis the finish screen retired that day
		// (docs/project/finish-screen-classification.md). History keeps all seven verdicts: it is a
		// forensic surface, not a summary, and per-row detail is what it is opened for. Only this
		// row's wording moved onto the assessment axis, and its keys now follow its token the way
		// the other six already did.
		RELEARNED:      { res: "hist_v_relearned", tip: "hist_vtip_relearned", cls: "histVMissClass", rank: 5 },
		EARLY_PRACTICE: { res: "hist_v_early",    tip: "hist_vtip_early",    cls: "histVQuietClass", rank: 6 },
		PRACTICED:      { res: "hist_v_sameDay",  tip: "hist_vtip_sameDay",  cls: "histVQuietClass", rank: 7 }
	};

	/** Where a row's cell has nothing to say — an em dash, dimmed. Never a zero: "no schedule was
	 *  drawn" and "comes back in 0 days" are different facts and only one of them is true. */
	function dashCell(cssClass) {
		return $("<span></span>").addClass(cssClass).addClass("histNilClass").text("—");
	}

	/**
	 * What the model made of the word this session — the finish screen's §9.1 line, as a chip.
	 * Null for a word the labels voided (an input anomaly, a content defect): no honest claim
	 * exists, and inventing a neutral one would be the claim.
	 */
	function verdictCell(cssClass, token) {
		var v = token && HIST_VERDICT[token];
		if (!v) { return dashCell(cssClass); }
		return $("<span></span>").addClass(cssClass)
			.append($('<span class="histVChipClass"></span>').addClass(v.cls)
				.assignTooltip(v.tip).text(getRes(v.res)));
	}

	/**
	 * The plant, before and after. The step is drawn only when the stage actually MOVED — that is
	 * the news, and a column of unchanged pairs would bury it; a held stage shows its single plant,
	 * quietly, so the column still says where the word stands. Written through .html() because
	 * jQuery 1.9's $("<svg>") builds HTML-namespace nodes that never render (the garden.js:15 trap).
	 */
	function growthCell(cssClass, before, after) {
		// Stage 0 after the review means there is no plant: a first-ever miss plants nothing (the
		// history is still logged, which is why the row exists at all). Drawing the germination
		// shoot there would show a seed that was never sown.
		if (after == null || after < 1 || !window.GARDEN) { return dashCell(cssClass); }
		var $el = $("<span></span>").addClass(cssClass);
		// "germ" (Ready) and 0 (the v2 sprout) became different drawings on 2026-08-25, so this
		// mapping is now a real choice rather than an alias — and "germ" is the right one here.
		// These stages come from user_term_review rows, which record the SHIPPED LADDER's numbers
		// even while growth v2 owns the plant (GardenService writes plan.stageBefore/After, not
		// v2's), and in the ladder's numbering a 0 means no plant at all. Do not "fix" this to 0
		// without changing what that column is fed.
		function plant(stage) {
			return GARDEN.plantSvg(stage >= 1 ? stage : "germ", "pl-plant-sm", true);
		}
		if (before != null && after > before) {
			return $el.addClass("histGrewClass")
				.assignTooltip("hist_tip_grew")
				.html(plant(before) + '<span class="histGrowArrowClass">→</span>' + plant(after));
		}
		return $el.addClass("histHeldClass").html(plant(after));
	}

	/**
	 * When the word comes back — the number the growth strip counted down to, kept per session so a
	 * learner can read the whole staircase down a word's history. `redrawn === false` means this
	 * review did NOT draw a new promise (a same-day repeat, an early practice): the date is the one
	 * that was already standing, and marking it is what stops an untouched date from reading as a
	 * shrinking interval.
	 */
	function nextCell(cssClass, days, dueMs, redrawn) {
		if (days == null) { return dashCell(cssClass); }
		var held = redrawn === false;
		var $el = $("<span></span>").addClass(cssClass)
			.text(strFormat(getRes("hist_days_short"), days));
		if (held) {
			$el.addClass("histHeldDueClass")
				.append($('<span class="histHeldMarkClass"></span>').text("="));
		}
		// A function tooltip, so the DATE those days count to rides in it — the number answers "how
		// long", the date answers "when", and a column has room for only one of them.
		$el.assignTooltip(function () {
			return strFormat(getRes(held ? "hist_tip_next_held" : "hist_tip_next_drawn"),
				dueMs ? fmtDate(dueMs) : "—");
		});
		return $el;
	}

	/** Sorting a column of plants: the words that MOVED first, deepest step at the top, then by the
	 *  stage they landed on. One number, so the shared comparator can stay numeric. Null on exactly
	 *  what the cell draws as an em dash — a sort must not rank a row by a number the row does not
	 *  show, and "no data sinks to the end" is the table's rule for all three of these columns. */
	function growthValue(before, after) {
		if (after == null || after < 1) { return null; }
		return (before != null ? after - before : 0) * 10 + after;
	}

	function wordRow(word, showReplays) {
		// A long phrase or a long meaning is ellipsised in its column, and the hover is where it
		// can be read in full. That used to be a native title= on the whole row — an OS tooltip
		// firing under the engine's own boxes whenever the pointer crossed a cell that has one
		// (verdict, growth, next), and firing even when nothing was actually cut off. It is the
		// app's clip tip now (main.js CLIP_TIP_CLASS): our chrome, on the clipped label itself,
		// and only while it IS clipped.
		var $text = $('<span class="histWordTextClass ' + CLIP_TIP + '"></span>').text(word.term);
		var $el = $('<button type="button" class="histWordClass"></button>').append($text);
		// What the session drilled this word AS. Without it the table is a page of numbers about
		// words you have to remember the meaning of — and remembering it is the thing being tested.
		$el.append($('<span class="histWMeaningClass ' + CLIP_TIP + '"></span>').text(word.meaning || ""));
		$el.append(numCell("histWCellShownClass", word.presented));
		$el.append(numCell("histWCellRightClass", word.correct));
		// The times the answer had to be REVEALED — the timer ran out, or the learner asked to see
		// it. Not the wrong Enters: a wrong answer you then get right is one showing you got right,
		// and printing the retries beside the showings made a column that could not be added up
		// (a word shown twice reading "right 2, wrong 1"). Shown = right + wrong now, and holds.
		$el.append(nilCell("histWCellWrongClass", word.timeouts));
		// Help asked for, not a mistake made — so it is a plain counter and its zero is dimmed like
		// the other two, rather than being scored as anything.
		if (showReplays) {
			$el.append(nilCell("histWCellReplaysClass", word.replays));
		}
		// The list's own accuracy rule, per word — and with the columns beside it now meaning what
		// they say, it is exactly RIGHT ÷ SHOWN. Kept even though it is derivable from the two: "4
		// of 6" and "2 of 2" compare at a glance as 67% and 100% and not otherwise.
		$el.append(numCell("histWCellAccClass", fmtAccuracy({
			totalCorrect: word.correct, totalTimeouts: word.timeouts
		})));
		// A word answered right in 2.4s on average, or an em dash when it was never answered right
		// at all — the same rule and the same one decimal as the session's own "typical" column.
		$el.append(numCell("histWCellTypicalClass",
			word.avgMs != null ? (word.avgMs / 1000).toFixed(1) + "s" : "—"));
		// Right the FIRST time it was shown — the word you already knew. A star rather than a second
		// ✓: two identical marks in neighbouring columns are read as one, and the shape is what tells
		// them apart at a glance. Same green, because both are good news of the same family.
		var $firstTry = $('<span class="histWCellFirstTryClass"></span>');
		if (word.firstTry) {
			$firstTry.addClass("histMarkClass").attr("aria-hidden", "true").html(plIcon("star"));
		}
		$el.append($firstTry);
		// The finish screen's own "flawless" read, per word. A quiet mark, not a badge: it is good
		// news about a word, not a thing to act on. The heading names the column, the key beside the
		// filter says what it means. The cell is always here so the column never goes ragged.
		var $flawless = $('<span class="histWCellFlawlessClass"></span>');
		if (word.flawless) {
			$flawless.addClass("histFlawlessClass").attr("aria-hidden", "true").html(plIcon("check"));
		}
		$el.append($flawless);
		// What the app did with the word after the session: the plant it moved (or held), the date
		// it comes back on, and the §9.1 line the finish screen printed under it. All three are an
		// em dash for a word the session never credited — an unended quit, a first-ever miss — where
		// nothing was scheduled and there is nothing to report.
		$el.append(growthCell("histWCellGrowthClass", word.stageBefore, word.stageAfter));
		$el.append(nextCell("histWCellNextClass", word.intervalDays, word.dueMs, word.redrawn));
		$el.append(verdictCell("histWCellVerdictClass", word.verdict));
		// The word opens its own HISTORY as the next depth of this card — the same numbers this
		// row shows, told across every session, with the charts. The dictionary entry moved one
		// step further in: the history screen's Dictionary button raises it (as its own overlay,
		// the app's one door for "a word, over a modal", exactly as this click used to).
		$el.on("click", function () { openWordLevel(word.termId, word.term); });
		return {
			el: $el,
			word: word,
			apply: function (typed) {
				var re = WORDFILTER.regex(typed, pair.to);
				var hit = !re || re.test(word.term);
				$el.toggle(hit);
				if (hit) { WORDFILTER.paint($text, word.term, typed, pair.to); }
				return hit;
			}
		};
	}

	function filterBox(apply) {
		var $input = $("<input type='text' size='1' maxlength='40'/>")
			.addClass("wlFilterInputClass histFilterInputClass")
			.attr("placeholder", getRes("wlFilterPlaceholder"))
			.on("input", function () { apply($.trim($input.val())); })
			.on("keydown", function (e) {
				if (e.which === PL.keys.ESCAPE) {
					// ESC clears the box before it closes anything — the same two-step the
					// word-list boxes use, so a typed filter is never lost to a reflex Esc.
					if ($.trim($input.val())) {
						$input.val("");
						apply("");
						e.stopPropagation();
					}
				}
				// The card's own keys must not reach past a field being typed into.
				e.stopPropagation();
			});
		var $clear = $("<button type='button' class='pl-btn-icon-round wlFilterClearClass' tabindex='-1'></button>")
			.html(plIcon("x"))
			.on("mousedown", function (e) { e.preventDefault(); })   // don't steal the box's focus
			.on("click", function () {
				$input.val("");
				apply("");
				$input.focus();
			});
		// No autofocus: this screen is opened to read a session's numbers, and stealing the caret
		// into a filter box would put a cursor where the eye is not. The box is one click away.
		return $("<span class='wlFilterWrapClass histFilterWrapClass'></span>")
			.append($input).append($clear);
	}

	// ── the words root: every practiced word, lifetime numbers, trends ───────────────────────
	// The "words mastered" tile's own screen (openWords). One FLAT table, no bands: a word
	// without the ✓ only means every session it sat in was stopped early — an accident of
	// endings, not a verdict about the word — so splitting on it would dress an interruption up
	// as a learning fact. Stubbornness is something you SORT for instead: by Wrong, by Typical.

	function loadWordStats() {
		$.ajax({
			url: "api/word-stats",
			type: "GET",
			data: { langFrom: pair.from, langTo: pair.to },
			dataType: "json"
		}).done(function (data) {
			if (!layer) { return; }
			wordsData = (data && data.words) || [];
			if (stack.length === 1) { paintLevel(); }
		}).fail(function () {
			if (!layer) { return; }
			wordsData = "failed";
			if (stack.length === 1) { paintLevel(); }
		});
	}

	function fmtDate(ms) {
		return new Date(ms).toLocaleDateString(localeTag(), { year: "numeric", month: "short", day: "numeric" });
	}

	/** Milliseconds as seconds for a chart AXIS — whole above 10s, one decimal under. */
	function fmtMsAxis(ms) {
		var s = ms / 1000;
		return (s >= 10 || s === Math.round(s) ? Math.round(s) : s.toFixed(1)) + "s";
	}

	function fmtMsVal(ms) {
		return (ms / 1000).toFixed(1) + "s";
	}

	/** A review gap for the spacing chart — days, or hours below one day. Zero says 0: the
	 *  axis baseline must not claim an hour that never passed. */
	function fmtDaysVal(d) {
		if (d === 0) { return "0"; }
		if (d >= 10) { return Math.round(d) + "d"; }
		if (d >= 1) { return (d === Math.round(d) ? d : d.toFixed(1)) + "d"; }
		return Math.max(1, Math.round(d * 24)) + "h";
	}

	/** A chart in a titled box; fill the host AFTER the box is in the DOM (charts measure it). */
	function chartBox(titleKey, tipKey) {
		var $host = $('<div class="chartHostClass"></div>');
		var $el = $('<div class="chartBoxClass"></div>')
			.append($('<div class="chartTitleClass"></div>').text(getRes(titleKey)).assignTooltip(tipKey))
			.append($host);
		return { el: $el, host: $host };
	}

	function chartEmpty($host, text) {
		$host.empty().append($('<div class="chartEmptyClass"></div>').text(text));
	}

	// The pair's two "many words over time" charts, drawn from the SESSION list — the same rows
	// the sessions door shows, read as two time series. Filled once that list has arrived (both
	// doors request it on open); until then the boxes say loading and the arrival repaints.
	function renderTrends() {
		var speed = chartBox("hist_ws_chart_speed", "hist_ws_tip_chart_speed");
		var grown = chartBox("hist_ws_chart_mastered", "hist_ws_tip_chart_mastered");
		var $el = $('<div class="chartsGridClass histTrendsClass"></div>')
			.append(speed.el).append(grown.el);
		return {
			el: $el,
			fill: function () {
				if (!$.isArray(rowsData)) {
					var text = rowsData === "failed" ? getRes("hist_failed") : getRes("hist_loading");
					chartEmpty(speed.host, text);
					chartEmpty(grown.host, text);
					return;
				}
				var asc = rowsData.slice().reverse();   // the list is newest first
				var speedPts = [];
				for (var i = 0; i < asc.length; i++) {
					var s = asc[i];
					if (s.medianAnswerMs != null) {
						speedPts.push({ x: s.startedAtMs, y: s.medianAnswerMs,
							tip: fmtDate(s.startedAtMs) + " · " + fmtTypical(s) });
					}
				}
				// DISTINCT words, counted at each word's FIRST mastering — not the per-session
				// masterings summed up, which count a word once per session it was mastered in.
				// This is what makes the curve's endpoint equal the tile that opened the card.
				var firsts = [];
				for (var w = 0; w < wordsData.length; w++) {
					if (wordsData[w].firstMasteredMs != null) {
						firsts.push(wordsData[w].firstMasteredMs);
					}
				}
				firsts.sort(function (a, b) { return a - b; });
				var cumPts = [];
				for (var f = 0; f < firsts.length; f++) {
					if (cumPts.length && firsts[f] === cumPts[cumPts.length - 1].x) {
						cumPts[cumPts.length - 1].y = f + 1;
						cumPts[cumPts.length - 1].tip = fmtDate(firsts[f]) + " · " + (f + 1);
					} else {
						cumPts.push({ x: firsts[f], y: f + 1,
							tip: fmtDate(firsts[f]) + " · " + (f + 1) });
					}
				}
				if (speedPts.length < 2) {
					chartEmpty(speed.host, getRes("hist_chart_few"));
				} else {
					WORDCHARTS.render(speed.host[0], { height: 160, yFmt: fmtMsAxis,
						series: [{ kind: "line", pts: speedPts }] });
				}
				if (cumPts.length < 2) {
					chartEmpty(grown.host, getRes("hist_chart_few"));
				} else {
					WORDCHARTS.render(grown.host[0], { height: 160,
						yFmt: function (v) { return String(Math.round(v)); },
						series: [{ kind: "step", pts: cumPts, r: 2.5 }] });
				}
			}
		};
	}

	// One ordering rule per column of the lifetime word table.
	function wsValue(word, key) {
		switch (key) {
			case "word": return word.term;
			case "meaning": return word.meaning || "";
			case "sessions": return word.sessions;
			case "shown": return word.shown;
			case "right": return word.right;
			case "wrong": return word.wrong;
			case "accuracy": return word.shown > 0 ? word.right / word.shown : null;
			case "typical": return word.typicalMs;
			case "trend": return word.trendPct;
			case "mastered": return word.mastered ? 1 : 0;
		}
		return null;
	}

	function renderWordStats($into) {
		if (wordsData === null) {
			$into.append(renderState("", getRes("hist_loading")));
			return;
		}
		if (wordsData === "failed") {
			$into.append(renderState("histStateErrClass", getRes("hist_failed")));
			return;
		}
		if (!wordsData.length) {
			$into.append(renderState("", getRes("hist_ws_empty")));
			return;
		}

		// Same column layout as the session screen: trends fixed on top, the table below them
		// the one scrolling surface.
		$into.addClass("histBodyDetailClass");
		var trends = renderTrends();
		$into.append(trends.el);

		var masteredCount = 0;
		var rows = [];
		for (var i = 0; i < wordsData.length; i++) {
			if (wordsData[i].mastered) { masteredCount++; }
			rows.push(wsRow(wordsData[i]));
		}

		var $sec = $('<div class="histWordsSecClass"></div>');
		var $rowsBox = $('<div class="histWsRowsClass"></div>');
		var $scroller = $('<div class="histWordsClass"></div>');
		var $empty = renderState("histNoMatchClass", getRes("hist_words_noMatch")).hide();
		var sort = sortable({
			id: "words",
			value: function (r, key) { return wsValue(r.word, key); },
			tie: function (r) { return r.word.term; }
		});
		var $legend = wsLegend(sort);

		// "1234 words · 987 mastered", and "12 of 1234" once a filter narrows it — the header is
		// where the tile's number and the funnel's remainder become checkable against real rows.
		var $count = $('<span class="histWsCountClass"></span>');
		function setCount(shown) {
			// ⛔ countTxt is EITHER a number or the composed phrase "12 of 1,234" — which is why
			// hist_ws_count cannot take a plural rule until it is restructured, and why fmtNum is
			// applied to each number here rather than to the slot (i18n-runtime-grammar.md §4.2).
			var countTxt = shown === rows.length
				? fmtNum(rows.length)
				: fmtNum(shown) + " " + strFormat(getRes("hist_of"), fmtNum(rows.length));
			$count.text(strFormat(getRes("hist_ws_count"), countTxt, fmtNum(masteredCount)));
		}
		setCount(rows.length);

		var $tools = $('<div class="histFilterRowClass"></div>')
			.append(filterBox(function (typed) {
				var hits = 0;
				for (var r = 0; r < rows.length; r++) { hits += rows[r].apply(typed) ? 1 : 0; }
				$empty.toggle(hits === 0);
				$legend.toggle(hits > 0);
				setCount(hits);
			}))
			.append($count)
			.append($('<span class="histCheckKeyClass"></span>')
				.append($('<span class="histWsMasteredClass" aria-hidden="true"></span>').html(plIcon("check")))
				.append($("<span></span>").text(getRes("hist_ws_mastered_legend"))));

		// Sorting reorders the row NODES in place rather than repainting the level: a repaint
		// would throw away the typed filter, and the rows already carry their own show/hide.
		sort.repaint = function () {
			var sorted = sort.order(rows);
			for (var r = 0; r < sorted.length; r++) { $rowsBox.append(sorted[r].el); }
			sort.mark($legend);
		};
		sort.repaint();

		$sec.append($('<div class="histWordsHeadClass"></div>').append($tools))
			.append($scroller.append($legend).append($rowsBox).append($empty));
		$into.append($sec);
		trends.fill();
		if (window.SCROLL_PAGER) { SCROLL_PAGER.attach($scroller[0]); }
	}

	function wsLegend(sort) {
		return $('<div class="histWsLegendClass plStickyHeadClass"></div>')
			.append(sort.head("hist_wcol_word", "hist_ws_tip_word", "word", false))
			.append(sort.head("hist_wcol_meaning", "hist_tip_meaning", "meaning", false))
			.append(sort.head("hist_ws_col_sessions", "hist_ws_tip_sessions", "sessions", true))
			.append(sort.head("hist_col_shown", "hist_ws_tip_shown", "shown", true))
			.append(sort.head("hist_wcol_right", "hist_ws_tip_right", "right", true))
			.append(sort.head("hist_col_wrong", "hist_ws_tip_wrong", "wrong", true))
			.append(sort.head("hist_col_accuracy", "hist_ws_tip_accuracy", "accuracy", true))
			.append(sort.head("hist_col_typical", "hist_ws_tip_typical", "typical", true))
			.append(sort.head("hist_ws_col_trend", "hist_ws_tip_trend", "trend", true))
			.append(sort.head("hist_ws_col_mastered", "hist_ws_tip_mastered", "mastered", true));
	}

	function trendCell(word) {
		var $c = $('<span class="histWsCellTrendClass"></span>');
		if (word.trendPct == null) {
			return $c.addClass("histNilClass").text("—");
		}
		if (word.trendPct === 0) {
			return $c.text("0%");
		}
		var faster = word.trendPct < 0;
		// Faster wears the good-news green; slower wears the diff's ORANGE, not its red — taking
		// longer than you used to is worth a look, but it is not a mistake.
		return $c.addClass(faster ? "histTrendFastClass" : "histTrendSlowClass")
			.text((faster ? "▼ " : "▲ ") + Math.abs(word.trendPct) + "%");
	}

	function wsRow(word) {
		// Clip tips rather than a row-wide native title — see wordRow.
		var $text = $('<span class="histWordTextClass ' + CLIP_TIP + '"></span>').text(word.term);
		var $el = $('<button type="button" class="histWsRowClass"></button>')
			.append($text)
			.append($('<span class="histWMeaningClass histWsCellMeaningClass ' + CLIP_TIP + '"></span>')
				.text(word.meaning || ""))
			.append(numCell("histWsCellSessionsClass", word.sessions))
			.append(numCell("histWsCellShownClass", word.shown))
			.append(numCell("histWsCellRightClass", word.right))
			.append(nilCell("histWsCellWrongClass", word.wrong))
			.append(numCell("histWsCellAccClass", fmtAccuracy({
				totalCorrect: word.right, totalTimeouts: word.wrong
			})))
			.append(numCell("histWsCellTypicalClass",
				word.typicalMs != null ? fmtMsVal(word.typicalMs) : "—"))
			.append(trendCell(word));
		var $mastered = $('<span class="histWsCellMasteredClass"></span>');
		if (word.mastered) {
			$mastered.addClass("histWsMasteredClass").attr("aria-hidden", "true").html(plIcon("check"));
		}
		$el.append($mastered);
		$el.on("click", function () { openWordLevel(word.termId, word.term); });
		return {
			el: $el,
			word: word,
			apply: function (typed) {
				var re = WORDFILTER.regex(typed, pair.to);
				var hit = !re || re.test(word.term);
				$el.toggle(hit);
				if (hit) { WORDFILTER.paint($text, word.term, typed, pair.to); }
				return hit;
			}
		};
	}

	// ── one word's history: header, charts, its sessions ─────────────────────────────────────

	function openWordLevel(termId, termText) {
		var title = termText;
		pushLevel({
			title: title,
			render: function ($into) { renderWordHistory($into, termId, termText); }
		});
		if (wordHistCache[termId]) { return; }

		var tz = "";
		try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch (e) { tz = ""; }
		$.ajax({
			url: "api/word-history",
			type: "GET",
			data: { termId: termId, langFrom: pair.from, langTo: pair.to, tz: tz },
			dataType: "json"
		}).done(function (data) {
			if (!layer) { return; }
			wordHistCache[termId] = data || "failed";
			repaintIfShowing(title);
		}).fail(function () {
			if (!layer) { return; }
			wordHistCache[termId] = "failed";
			repaintIfShowing(title);
		});
	}

	function renderWordHistory($into, termId, termText) {
		var d = wordHistCache[termId];
		if (!d) {
			$into.append(renderState("", getRes("hist_loading")));
			return;
		}
		if (d === "failed") {
			$into.append(renderState("histStateErrClass", getRes("hist_failed")));
			return;
		}

		// The header: the word itself and what it means. The word IS the door to its dictionary
		// entry — it used to be a "Dictionary" pill beside it, which spent a control on saying
		// what the app already says everywhere else: a headword opens its entry (._termWordClass
		// in the dictionary list, the tokenized heads inside a rich card). The entry opens as its
		// own overlay on top of this card (the app's one door for "a word, over a modal"; one Esc
		// returns here with everything as it was).
		var $head = $('<div class="histWhHeadClass"></div>')
			.append($('<span class="histWhWordClass"></span>')
				.text(d.term)
				.assignTooltip("hist_wh_tip_dictionary")
				.on("click", function () {
					if (window.openTermRichPopup) { window.openTermRichPopup(d.term); }
				}));
		if (d.meaning) {
			$head.append($('<span class="histWhMeaningClass"></span>').text(d.meaning));
		}
		$into.append($head);

		if (!d.sessionRows.length) {
			$into.append(renderState("", getRes("hist_wh_none")));
			return;
		}

		// Lifetime tiles — the same figures this word's row in the statistics table carries.
		var $tiles = $('<div class="histTilesClass"></div>');
		$tiles.append(statTile(d.sessions, getRes("hist_ws_col_sessions"), "hist_ws_tip_sessions"));
		$tiles.append(statTile(d.shown, getRes("hist_col_shown"), "hist_ws_tip_shown"));
		$tiles.append(statTile(fmtAccuracy({ totalCorrect: d.right, totalTimeouts: d.wrong }),
			getRes("hist_col_accuracy"), "hist_ws_tip_accuracy"));
		if (d.typicalMs != null) {
			$tiles.append(statTile(fmtMsVal(d.typicalMs), getRes("hist_col_typical"),
				"hist_ws_tip_typical"));
		}
		$into.append($tiles);

		var $facts = $('<div class="histFactsClass"></div>');
		if (d.firstMs > 0) {
			$facts.append(factCell("hist_wh_firstSeen", fmtDate(d.firstMs), "hist_wh_tip_firstSeen"));
			$facts.append(factCell("hist_wh_lastSeen", fmtDate(d.lastMs), "hist_wh_tip_lastSeen"));
		}
		$facts.append(factCell("hist_fact_correct", d.right, "hist_ws_tip_right"));
		$facts.append(factCell("hist_fact_wrong", d.wrong, "hist_ws_tip_wrong"));
		if (d.replays > 0) {
			$facts.append(factCell("hist_fact_replays", d.replays, "hist_tip_replays"));
		}
		// The word's standing in the garden — only when it HAS one: a word drilled before the
		// garden existed, or dropped before mastering, simply has no plant to report.
		if (d.gardenStage != null && d.gardenStage > 0) {
			$facts.append(factCell("hist_wh_stage",
				d.gardenStage + " " + strFormat(getRes("hist_of"), 7), "hist_wh_tip_stage"));
			if (d.dueMs != null) {
				var due = fmtDate(d.dueMs)
					+ (d.dueMs < Date.now() ? " · " + getRes("hist_wh_overdue") : "");
				$facts.append(factCell("hist_wh_due", due, "hist_wh_tip_due"));
			}
			if (d.lapses != null && d.lapses > 0) {
				$facts.append(factCell("hist_wh_lapses", d.lapses, "hist_wh_tip_lapses"));
			}
		}
		$into.append($facts);
		$into.append(articleChip(d));

		// The four charts. Hosts must be in the DOM before render — a chart measures its box.
		var speed = chartBox("hist_wh_chart_speed", "hist_wh_tip_chart_speed");
		var gaps = chartBox("hist_wh_chart_gaps", "hist_wh_tip_chart_gaps");
		var stage = chartBox("hist_wh_chart_stage", "hist_wh_tip_chart_stage");
		var acc = chartBox("hist_wh_chart_acc", "hist_wh_tip_chart_acc");
		// A FIFTH box only when this word has ever been spoken. The other four are drawn empty
		// ("not enough yet") because every drilled word is on its way to having them; pronunciation
		// is an optional practice, and an empty frame on every word would advertise a feature at
		// the cost of the four charts that do have something to say.
		var spoken = (d.spoken && d.spoken.length)
			? chartBox("hist_wh_chart_spoken", "hist_wh_tip_chart_spoken") : null;
		var $grid = $('<div class="chartsGridClass histWhChartsClass"></div>')
			.append(speed.el).append(gaps.el).append(stage.el).append(acc.el);
		if (spoken) { $grid.append(spoken.el); }
		$into.append($grid);

		fillSpeedChart(speed.host, d);
		fillGapsChart(gaps.host, d);
		fillStageChart(stage.host, d);
		fillAccuracyChart(acc.host, d);
		if (spoken) { fillSpokenChart(spoken.host, d); }

		// The word's sessions, newest first like the session list, each row a door into that
		// session's own screen — the same walk the sessions door offers, entered from the word.
		// Newest-first is a real ordering, so the When column wears the arrow for it from the
		// start; sorting by Typical is how "when did this word get fast" gets answered.
		var $sec = $('<div class="histWhSecClass"></div>');
		var sort = sortable({
			id: "wordSessions",
			value: whValue,
			tie: function (s) { return -s.startedAtMs; },
			initial: { key: "when", numeric: true, desc: true }
		});
		var $legend = $('<div class="histWhLegendClass plStickyHeadClass"></div>')
			.append(sort.head("hist_wh_col_when", "hist_wh_tip_when", "when", true))
			.append(sort.head("hist_fact_direction", "hist_tip_direction", "direction", false))
			.append(sort.head("hist_col_shown", "hist_tip_wshown", "shown", true))
			.append(sort.head("hist_wcol_right", "hist_tip_wright", "right", true))
			.append(sort.head("hist_col_wrong", "hist_tip_wwrong", "wrong", true))
			.append(sort.head("hist_col_typical", "hist_tip_wtypical", "typical", true))
			.append(sort.head("hist_wcol_firstTry", "hist_tip_firstTry", "firstTry", true))
			.append(sort.head("hist_col_flawless", "hist_tip_wflawless", "flawless", true))
			// The same three the session's word table carries — and read DOWN this table they are
			// the thing the transpose exists for: one word's whole schedule, session by session,
			// growing (or not) in front of the learner.
			.append(sort.head("hist_wcol_growth", "hist_tip_growth", "growth", true))
			.append(sort.head("hist_wcol_next", "hist_tip_next", "next", true))
			.append(sort.head("hist_wcol_verdict", "hist_tip_verdict", "verdict", true));
		// The rows get their own box so a re-sort can empty it without taking the legend — and the
		// legend can then stay pinned where it is, which is the point of pinning it.
		var $rowsBox = $('<div class="histWhRowsClass"></div>');
		sort.repaint = function () {
			var rows = sort.order(d.sessionRows);
			$rowsBox.empty();
			for (var i = 0; i < rows.length; i++) { $rowsBox.append(whSessionRow(rows[i])); }
			sort.mark($legend);
		};
		sort.repaint();
		$sec.append($legend).append($rowsBox);
		$into.append($sec);
		if (window.SCROLL_PAGER) { SCROLL_PAGER.attach($into[0]); }
	}

	// One ordering rule per column of a word's own session table — the session-word columns by
	// their session-level names, plus WHEN it happened.
	function whValue(s, key) {
		switch (key) {
			case "when": return s.startedAtMs;
			case "direction": return fmtDirection(s);
			case "shown": return s.shown;
			case "right": return s.right;
			case "wrong": return s.wrong;
			case "typical": return s.avgMs;
			case "firstTry": return s.firstTry ? 1 : 0;
			case "flawless": return s.flawless ? 1 : 0;
			case "growth": return growthValue(s.stageBefore, s.stageAfter);
			case "next": return s.intervalDays;
			case "verdict": return HIST_VERDICT[s.verdict] ? HIST_VERDICT[s.verdict].rank : null;
		}
		return null;
	}

	function whSessionRow(s) {
		var $el = $('<button type="button" class="histWhRowClass"></button>')
			.toggleClass("histRowStoppedClass", s.outcome === "STOPPED");
		var $when = $('<span class="histWhCellWhenClass"></span>')
			.text(dayLabel(s) + " · " + fmtTime(s.startedAtMs));
		$el.append($when);
		var $dir = $('<span class="histWhCellDirClass"></span>').text(fmtDirection(s));
		if (s.outcome === "STOPPED") {
			$dir.append($('<span class="histStoppedChipClass"></span>').text(getRes("hist_stopped")));
		}
		$el.append($dir);
		$el.append(numCell("histWhCellShownClass", s.shown));
		$el.append(numCell("histWhCellRightClass", s.right));
		$el.append(nilCell("histWhCellWrongClass", s.wrong));
		$el.append(numCell("histWhCellTypicalClass", s.avgMs != null ? fmtMsVal(s.avgMs) : "—"));
		var $firstTry = $('<span class="histWhCellFirstTryClass"></span>');
		if (s.firstTry) {
			$firstTry.addClass("histMarkClass").attr("aria-hidden", "true").html(plIcon("star"));
		}
		$el.append($firstTry);
		var $flawless = $('<span class="histWhCellFlawlessClass"></span>');
		if (s.flawless) {
			$flawless.addClass("histFlawlessClass").attr("aria-hidden", "true").html(plIcon("check"));
		}
		$el.append($flawless);
		$el.append(growthCell("histWhCellGrowthClass", s.stageBefore, s.stageAfter));
		$el.append(nextCell("histWhCellNextClass", s.intervalDays, s.dueMs, s.redrawn));
		$el.append(verdictCell("histWhCellVerdictClass", s.verdict));
		// The full session row lives in the list both doors load on open; without it (still in
		// flight, or failed) the click simply does nothing rather than opening a half-told
		// session.
		$el.on("click", function () {
			if (!$.isArray(rowsData)) { return; }
			for (var i = 0; i < rowsData.length; i++) {
				if (rowsData[i].id === s.sessionId) { openDetail(rowsData[i]); return; }
			}
		});
		return $el;
	}

	// ── the word's charts ────────────────────────────────────────────────────────────────────

	/**
	 * Answer speed: the per-session typical time as the LINE (the trend), every individual
	 * showing as a quiet dot behind it (the evidence), and a red ✗ on the top edge for each
	 * showing that ended with the answer revealed — an event with no time of its own to place.
	 * Dots only exist where the raw attempt stream does (captured since 2026-07-24); older
	 * sessions still hold the line up on their own.
	 */
	function fillSpeedChart($host, d) {
		var line = [], dots = [], marks = [];
		for (var i = 0; i < d.sessionRows.length; i++) {
			var s = d.sessionRows[i];
			if (s.avgMs != null) {
				line.push({ x: s.startedAtMs, y: s.avgMs,
					tip: fmtDate(s.startedAtMs) + " · " + fmtMsVal(s.avgMs) });
			}
		}
		for (var j = 0; j < d.showings.length; j++) {
			var sh = d.showings[j];
			if (sh.correct && sh.completionMs != null) {
				dots.push({ x: sh.tsMs, y: sh.completionMs,
					tip: fmtDate(sh.tsMs) + " · " + fmtMsVal(sh.completionMs) });
			} else if (!sh.correct) {
				marks.push({ x: sh.tsMs, tip: fmtDate(sh.tsMs) + " · " + getRes("hist_wh_revealed") });
			}
		}
		if (line.length < 2 && dots.length < 3) {
			chartEmpty($host, getRes("hist_chart_few"));
			return;
		}
		WORDCHARTS.render($host[0], {
			height: 180,
			yFmt: fmtMsAxis,
			series: [
				{ kind: "dots", pts: dots, cls: "chDotSoftClass", r: 2.5 },
				{ kind: "line", pts: line },
				{ kind: "marks", pts: marks }
			]
		});
	}

	/**
	 * The gap before each practice of this word, in days — growing gaps are what a
	 * strengthening memory looks like from the outside. The last, outlined bar is the SCHEDULED
	 * gap: from the latest practice to the garden's next-due date — a promise, not a fact yet.
	 *
	 * <p>Over the bars runs what the app PROMISED at each of those sessions (changeset 0078's
	 * stored interval). The two are different facts and the difference is the readable part: the
	 * line above its bar is a word practiced sooner than it had to be, below it a word left waiting.
	 * Bar j is the gap that followed session j, so the promise drawn at session j sits at the same
	 * x — the line and the bars are the same events, seen forwards and backwards.
	 */
	function fillGapsChart($host, d) {
		var pts = [];
		var sched = [];
		var rows = d.sessionRows;
		var i;
		for (i = 1; i < rows.length; i++) {
			var gap = (rows[i].startedAtMs - rows[i - 1].startedAtMs) / 86400000;
			pts.push({ x: pts.length, y: Math.max(0.02, gap),
				tip: fmtDate(rows[i].startedAtMs) + " · " + fmtDaysVal(gap) });
		}
		if (d.dueMs != null && d.lastMs > 0 && d.dueMs > d.lastMs) {
			var next = (d.dueMs - d.lastMs) / 86400000;
			pts.push({ x: pts.length, y: next, cls: "chBarGhostClass",
				tip: getRes("hist_wh_due") + " · " + fmtDate(d.dueMs) + " · " + fmtDaysVal(next) });
		}
		// One segment per unbroken run of promises. A session that made none — it kept a date that
		// had already ripened, or planted nothing at all — BREAKS the line rather than being
		// bridged: a straight segment drawn over it would show a schedule in force where there was
		// none, which is the one thing the dashes are there to deny.
		for (i = 0; i < rows.length && i < pts.length; i++) {
			if (rows[i].intervalDays == null) {
				sched.push(null);
				continue;
			}
			sched.push({ x: i, y: rows[i].intervalDays,
				tip: getRes("hist_wh_scheduled") + " · " + fmtDate(rows[i].startedAtMs)
					+ " · " + fmtDaysVal(rows[i].intervalDays) });
		}
		var runs = [];
		for (i = 0; i < sched.length; i++) {
			if (sched[i] == null) { continue; }
			if (!i || sched[i - 1] == null) { runs.push([]); }
			runs[runs.length - 1].push(sched[i]);
		}
		if (pts.length < 2) {
			chartEmpty($host, getRes("hist_chart_few"));
			return;
		}
		WORDCHARTS.render($host[0], {
			height: 180,
			yFmt: function (v) { return fmtDaysVal(v); },
			xTicks: [],   // an index axis: the bars are "each next practice", dates live in the tips
			// The line LAST, so it paints — and takes its hover — over the bars it is compared to.
			// A one-point run still draws: its dot is the promise, and a lone dot is exactly what a
			// promise with nothing either side of it looks like.
			series: [{ kind: "bars", pts: pts }].concat(runs.map(function (run) {
				return { kind: "line", pts: run, cls: "chLineSchedClass", r: 2.5 };
			}))
		});
	}

	/**
	 * The garden ladder: the plant's stage after every counted review, held between reviews
	 * (step), a green dot where the review passed and a red one where the word slipped.
	 */
	function fillStageChart($host, d) {
		if (!d.reviews.length) {
			chartEmpty($host, getRes("hist_chart_few"));
			return;
		}
		var steps = [{ x: d.reviews[0].tsMs, y: d.reviews[0].stageBefore }];
		var dots = [];
		for (var i = 0; i < d.reviews.length; i++) {
			var r = d.reviews[i];
			steps.push({ x: r.tsMs, y: r.stageAfter });
			dots.push({ x: r.tsMs, y: r.stageAfter,
				cls: r.pass ? "chDotPassClass" : "chDotFailClass",
				tip: fmtDate(r.tsMs) + " · "
					+ getRes(r.pass ? "hist_wh_pass" : "hist_wh_fail")
					+ " · " + r.stageBefore + " → " + r.stageAfter });
		}
		WORDCHARTS.render($host[0], {
			height: 180,
			yMax: 7,
			yTicks: [0, 2, 4, 6],
			yFmt: function (v) { return String(v); },
			series: [
				{ kind: "step", pts: steps },
				{ kind: "dots", pts: dots, r: 3.5 }
			]
		});
	}

	/**
	 * Pronunciation: every attempt as a dot, the running BEST as a step line holding above them
	 * (docs/project/ai-features-plan.md §1 — "progress = per-word best over time").
	 *
	 * <p>Two series rather than one because a bare best line would be a staircase that only ever
	 * goes up, which says nothing about whether today went well. The dots are the practice; the
	 * line is the record. Both plot the WEAKEST-sound score, the number the popup's ring shows —
	 * the chart and the popup must not tell one learner two stories about one attempt.
	 *
	 * <p>Dots are coloured by the same three bands as the popup's washes, so green/amber/red mean
	 * the same thing here, on a typed answer's diff, and inside the practice popup.
	 */
	function fillSpokenChart($host, d) {
		var dots = [], best = [], top = null;
		for (var i = 0; i < d.spoken.length; i++) {
			var a = d.spoken[i];
			var cls = a.score >= 80 ? "chDotPassClass" : (a.score >= 60 ? "" : "chDotFailClass");
			var tip = fmtDate(a.tsMs) + " · " + a.score
				+ (a.dialect ? " · " + a.dialect : "");
			dots.push({ x: a.tsMs, y: a.score, cls: cls, tip: tip });
			if (top == null || a.score > top) { top = a.score; }
			best.push({ x: a.tsMs, y: top, tip: getRes("pronBest_label") + " · " + top });
		}
		WORDCHARTS.render($host[0], {
			height: 180,
			yMax: 100,
			yTicks: [0, 50, 100],
			yFmt: function (v) { return String(v); },
			series: [
				{ kind: "dots", pts: dots, r: 3.5 },
				{ kind: "step", pts: best }
			]
		});
	}

	/** Right ÷ shown, per session — the same rule as every accuracy figure on these screens. */
	function fillAccuracyChart($host, d) {
		var pts = [];
		for (var i = 0; i < d.sessionRows.length; i++) {
			var s = d.sessionRows[i];
			if (s.shown > 0) {
				pts.push({ x: pts.length, y: Math.round(s.right / s.shown * 100),
					tip: fmtDate(s.startedAtMs) + " · " + s.right + " "
						+ strFormat(getRes("hist_of"), s.shown) + " · "
						+ Math.round(s.right / s.shown * 100) + "%" });
			}
		}
		if (pts.length < 2) {
			chartEmpty($host, getRes("hist_chart_few"));
			return;
		}
		WORDCHARTS.render($host[0], {
			height: 180,
			yMax: 100,
			yTicks: [0, 50, 100],
			yFmt: function (v) { return v + "%"; },
			xTicks: [],
			series: [{ kind: "bars", pts: pts }]
		});
	}

	// ── the accuracy root: how the one percentage is made, and what moves it ─────────────────
	// Three answers to "why 87%?": is it improving (accuracy by session), how much of it is
	// recognition rather than drilling (known on sight), and which words drag it down (the
	// hardest-words list). Everything is derived from the two lists already in hand.

	/** Words shown at least this often qualify for the hardest-words list — below it a single
	 *  revealed answer produces a dramatic percentage that does not mean anything yet. */
	var HARD_MIN_SHOWN = 4;
	/** A diagnosis, not a second word table — the full sortable table lives one tile over,
	 *  behind "words mastered". */
	var HARD_MAX_ROWS = 15;

	/** Both roots below need both lists before they can say anything — unlike the words root,
	 *  neither has a table that could stand alone while the other list is still in flight. */
	function bothLoaded($into) {
		if (rowsData === "failed" || wordsData === "failed") {
			$into.append(renderState("histStateErrClass", getRes("hist_failed")));
			return false;
		}
		if (rowsData === null || wordsData === null) {
			$into.append(renderState("", getRes("hist_loading")));
			return false;
		}
		return true;
	}

	function renderAccuracy($into) {
		if (!bothLoaded($into)) { return; }
		if (!rowsData.length) {
			$into.append(renderState("", getRes("hist_empty")));
			return;
		}

		// Lifetime totals from the same rows the session list shows — the tile's own formula
		// (right ÷ (right + revealed)) over the whole history, so the headline here cannot
		// disagree with the tile that opened it.
		var right = 0, wrong = 0, firstTry = 0, wordSum = 0;
		for (var i = 0; i < rowsData.length; i++) {
			right += rowsData[i].totalCorrect;
			wrong += rowsData[i].totalTimeouts;
			firstTry += rowsData[i].firstTryWords;
			wordSum += rowsData[i].wordCount;
		}

		var $tiles = $('<div class="histTilesClass"></div>');
		$tiles.append(statTile(fmtAccuracy({ totalCorrect: right, totalTimeouts: wrong }),
			getRes("hist_col_accuracy"), "hist_acc_tip_accuracy"));
		$tiles.append(statTile(right, getRes("hist_fact_correct"), "hist_ws_tip_right"));
		$tiles.append(statTile(wrong, getRes("hist_fact_wrong"), "hist_ws_tip_wrong"));
		// The ★ verdict as a lifetime rate: of every word every session put in front of you,
		// the share answered right at FIRST sight. The gap between this and the headline
		// accuracy is roughly what the drilling itself contributes.
		if (wordSum > 0) {
			$tiles.append(statTile(Math.round(firstTry / wordSum * 100) + "%",
				getRes("hist_acc_firstTry_lbl"), "hist_acc_tip_firstTry"));
		}
		$into.append($tiles);

		// Same column layout as the words root: charts fixed on top, the list below them the
		// one scrolling surface.
		$into.addClass("histBodyDetailClass");
		var bySession = chartBox("hist_wh_chart_acc", "hist_acc_tip_chart_bySession");
		var onSight = chartBox("hist_acc_chart_firstTry", "hist_acc_tip_chart_firstTry");
		$into.append($('<div class="chartsGridClass histTrendsClass"></div>')
			.append(bySession.el).append(onSight.el));
		fillPairAccuracyChart(bySession.host);
		fillFirstTryChart(onSight.host);

		renderHardWords($into);
	}

	/**
	 * Right ÷ shown for each finished session, oldest first — the tile's formula told as a
	 * trend. Index-scaled like the word screen's own accuracy chart: sessions bunch on good
	 * evenings, and a time axis would stack a whole evening into one stripe. Dates ride in the
	 * tips.
	 */
	function fillPairAccuracyChart($host) {
		var pts = [];
		var asc = rowsData.slice().reverse();
		for (var i = 0; i < asc.length; i++) {
			var s = asc[i];
			var total = s.totalCorrect + s.totalTimeouts;
			if (total > 0) {
				var pct = Math.round(s.totalCorrect / total * 100);
				pts.push({ x: pts.length, y: pct,
					tip: fmtDate(s.startedAtMs) + " · " + s.totalCorrect + " "
						+ strFormat(getRes("hist_of"), total) + " · " + pct + "%" });
			}
		}
		if (pts.length < 2) {
			chartEmpty($host, getRes("hist_chart_few"));
			return;
		}
		WORDCHARTS.render($host[0], {
			height: 170,
			yMax: 100,
			yTicks: [0, 50, 100],
			yFmt: function (v) { return v + "%"; },
			xTicks: [],
			series: [{ kind: "bars", pts: pts }]
		});
	}

	/**
	 * The share of each session's words answered right at their very FIRST showing — the ★
	 * verdict as a trend, on a real time axis. Read against the accuracy bars beside it:
	 * accuracy says how the showings went, this says how many words needed no drilling at all.
	 */
	function fillFirstTryChart($host) {
		var pts = [];
		var asc = rowsData.slice().reverse();
		for (var i = 0; i < asc.length; i++) {
			var s = asc[i];
			if (s.wordCount > 0) {
				var pct = Math.round(s.firstTryWords / s.wordCount * 100);
				pts.push({ x: s.startedAtMs, y: pct,
					tip: fmtDate(s.startedAtMs) + " · " + s.firstTryWords + " "
						+ strFormat(getRes("hist_of"), s.wordCount) + " · " + pct + "%" });
			}
		}
		if (pts.length < 2) {
			chartEmpty($host, getRes("hist_chart_few"));
			return;
		}
		WORDCHARTS.render($host[0], {
			height: 170,
			yMax: 100,
			yTicks: [0, 50, 100],
			yFmt: function (v) { return v + "%"; },
			series: [{ kind: "line", pts: pts, r: 2.5 }]
		});
	}

	// The words that drag the number down — lowest accuracy first, among words shown often
	// enough for their percentage to mean something. A selection with a floor and a cap, not a
	// second word table: "your misses live in these nine words" is a different statement from a
	// page of every word you ever fumbled once.
	function renderHardWords($into) {
		var hard = [];
		for (var i = 0; i < wordsData.length; i++) {
			var w = wordsData[i];
			if (w.shown >= HARD_MIN_SHOWN && w.wrong > 0) { hard.push(w); }
		}
		hard.sort(function (a, b) {
			var c = (a.right / a.shown) - (b.right / b.shown);
			return c !== 0 ? c : b.wrong - a.wrong;
		});
		hard = hard.slice(0, HARD_MAX_ROWS);

		var $sec = $('<div class="histWordsSecClass"></div>');
		$sec.append($('<div class="histWordsHeadClass histHardHeadRowClass"></div>')
			.append($('<span class="histHardHeadClass"></span>')
				.text(strFormat(getRes("hist_acc_hard_head"), hard.length)))
			.append($('<span class="histHardSubClass"></span>')
				.text(strFormat(getRes("hist_acc_hard_sub"), HARD_MIN_SHOWN))));

		if (!hard.length) {
			$sec.append(renderState("", getRes("hist_acc_hard_none")));
			$into.append($sec);
			return;
		}

		// The rows can be reordered, but WHICH rows these are never changes: the fifteen are
		// chosen by accuracy before anything here runs, and sorting them by Wrong asks "and of
		// those fifteen, which cost me the most showings" — a second question about one set, not
		// a different selection. That is why the heading above says how the set was picked.
		var sort = sortable({
			id: "hard",
			value: wsValue,
			tie: function (w) { return w.term; },
			initial: { key: "accuracy", numeric: true, desc: false }
		});
		var $scroller = $('<div class="histWordsClass"></div>');
		var $legend = $('<div class="histHardLegendClass plStickyHeadClass"></div>')
			.append(sort.head("hist_wcol_word", "hist_ws_tip_word", "word", false))
			.append(sort.head("hist_wcol_meaning", "hist_tip_meaning", "meaning", false))
			.append(sort.head("hist_col_shown", "hist_ws_tip_shown", "shown", true))
			.append(sort.head("hist_col_wrong", "hist_ws_tip_wrong", "wrong", true))
			.append(sort.head("hist_col_accuracy", "hist_ws_tip_accuracy", "accuracy", true));
		var $rowsBox = $("<div></div>");
		sort.repaint = function () {
			var rows = sort.order(hard);
			$rowsBox.empty();
			for (var h = 0; h < rows.length; h++) { $rowsBox.append(hardRow(rows[h])); }
			sort.mark($legend);
		};
		sort.repaint();
		$scroller.append($legend).append($rowsBox);
		$sec.append($scroller);
		$into.append($sec);
		if (window.SCROLL_PAGER) { SCROLL_PAGER.attach($scroller[0]); }
	}

	function hardRow(word) {
		// Clip tips rather than a row-wide native title — see wordRow.
		return $('<button type="button" class="histHardRowClass"></button>')
			.append($('<span class="histWordTextClass ' + CLIP_TIP + '"></span>').text(word.term))
			.append($('<span class="histWMeaningClass ' + CLIP_TIP + '"></span>').text(word.meaning || ""))
			.append(numCell("histHardCellClass", word.shown))
			.append(numCell("histHardCellClass", word.wrong))
			.append(numCell("histHardCellClass", fmtAccuracy({
				totalCorrect: word.right, totalTimeouts: word.wrong
			})))
			.on("click", function () { openWordLevel(word.termId, word.term); });
	}

	// ── the time root: where the studied hours went ──────────────────────────────────────────
	// This level scrolls as one page (like a word's own screen): tiles, a fact strip and two
	// charts, no table — the sessions themselves already have a door of their own.

	/** Lifetime study time in the My Progress tile's own units: minutes under an hour, one
	 *  decimal of hours above. Per-SESSION figures keep fmtDuration's m/s — a session is read
	 *  in minutes, a lifetime in hours. */
	function fmtStudiedTotal(sec) {
		if (sec <= 0) { return "—"; }
		if (sec < 60) { return Math.round(sec) + "s"; }
		if (sec < 3600) { return Math.round(sec / 60) + "m"; }
		return (sec / 3600).toFixed(1) + "h";
	}

	/** A study-time AXIS, fed minutes: whole minutes under an hour, hours above. */
	function fmtMinAxis(min) {
		if (min >= 60) {
			var h = min / 60;
			return (h === Math.round(h) ? h : h.toFixed(1)) + "h";
		}
		return Math.round(min) + "m";
	}

	function renderTime($into) {
		if (!bothLoaded($into)) { return; }
		if (!rowsData.length) {
			$into.append(renderState("", getRes("hist_empty")));
			return;
		}

		var totalSec = 0;
		var longest = null;
		var byDay = {};      // learning day ISO → seconds practiced on it
		var dayOrder = [];
		var durations = [];
		for (var i = 0; i < rowsData.length; i++) {
			var s = rowsData[i];
			totalSec += s.durationSec;
			durations.push(s.durationSec);
			if (!longest || s.durationSec > longest.durationSec) { longest = s; }
			if (s.dayIso) {
				if (byDay[s.dayIso] == null) {
					byDay[s.dayIso] = 0;
					dayOrder.push(s.dayIso);
				}
				byDay[s.dayIso] += s.durationSec;
			}
		}
		// The TYPICAL session is the median, the same middle-value rule every "typical" on
		// these screens follows — one marathon evening must not claim to be the norm.
		durations.sort(function (a, b) { return a - b; });
		var mid = durations.length >> 1;
		var medianSec = durations.length % 2 === 1
			? durations[mid]
			: Math.round((durations[mid - 1] + durations[mid]) / 2);

		var masteredCount = 0;
		for (var w = 0; w < wordsData.length; w++) {
			if (wordsData[w].mastered) { masteredCount++; }
		}

		var $tiles = $('<div class="histTilesClass"></div>');
		$tiles.append(statTile(fmtStudiedTotal(totalSec), getRes("stats_label_studied"),
			"hist_time_tip_total"));
		$tiles.append(statTile(rowsData.length, getRes("stats_label_sessions"),
			"hist_time_tip_sessions"));
		if (medianSec > 0) {
			$tiles.append(statTile(fmtDuration(medianSec), getRes("hist_time_typical_lbl"),
				"hist_time_tip_typical"));
		}
		if (dayOrder.length > 0) {
			$tiles.append(statTile(dayOrder.length, getRes("hist_time_days_lbl"),
				"hist_time_tip_days"));
		}
		$into.append($tiles);

		var $facts = $('<div class="histFactsClass"></div>');
		if (longest && longest.durationSec > 0) {
			$facts.append(factCell("hist_time_fact_longest",
				fmtDuration(longest.durationSec) + " · " + fmtDay(longest.dayIso),
				"hist_time_tip_longest"));
		}
		if (dayOrder.length > 0 && totalSec > 0) {
			$facts.append(factCell("hist_time_fact_perDay",
				fmtStudiedTotal(Math.round(totalSec / dayOrder.length)),
				"hist_time_tip_perDay"));
		}
		// What an hour of practice buys: total time over DISTINCT words mastered — the panel
		// tile's own count, so the two screens quote one number. Falling over time is the win,
		// which is why the tip says to read it against the charts below.
		if (masteredCount > 0 && totalSec > 0) {
			$facts.append(factCell("hist_time_fact_perWord",
				fmtDuration(Math.round(totalSec / masteredCount)),
				"hist_time_tip_perWord"));
		}
		$into.append($facts);

		var byDayBox = chartBox("hist_time_chart_byDay", "hist_time_tip_chart_byDay");
		var totalBox = chartBox("hist_time_chart_total", "hist_time_tip_chart_total");
		$into.append($('<div class="chartsGridClass histTimeChartsClass"></div>')
			.append(byDayBox.el).append(totalBox.el));
		fillTimeByDayChart(byDayBox.host, byDay, dayOrder);
		fillTimeTotalChart(totalBox.host);
		if (window.SCROLL_PAGER) { SCROLL_PAGER.attach($into[0]); }
	}

	/**
	 * Minutes practiced on each learning day, as bars on a REAL calendar axis — a break in the
	 * habit shows as the gap it was, which an index axis would quietly close up. Days are the
	 * app's learning days (03:00 cut), the same buckets the streak counts.
	 */
	function fillTimeByDayChart($host, byDay, dayOrder) {
		var pts = [];
		for (var i = 0; i < dayOrder.length; i++) {
			var iso = dayOrder[i];
			// Parsed at NOON local, like every dayIso on these screens — bare ISO parses as
			// UTC midnight and lands on the previous day west of Greenwich.
			pts.push({ x: new Date(iso + "T12:00:00").getTime(), y: byDay[iso] / 60,
				tip: fmtDay(iso) + " · " + fmtStudiedTotal(byDay[iso]) });
		}
		if (pts.length < 2) {
			chartEmpty($host, getRes("hist_chart_few"));
			return;
		}
		WORDCHARTS.render($host[0], {
			height: 170,
			yFmt: fmtMinAxis,
			series: [{ kind: "bars", pts: pts }]
		});
	}

	/** Hours studied, added up session by session — a steady habit reads as a steady slope. */
	function fillTimeTotalChart($host) {
		var asc = rowsData.slice().reverse();
		var pts = [];
		var cum = 0;
		for (var i = 0; i < asc.length; i++) {
			cum += asc[i].durationSec;
			pts.push({ x: asc[i].startedAtMs, y: cum / 3600,
				tip: fmtDate(asc[i].startedAtMs) + " · " + fmtStudiedTotal(cum) });
		}
		if (pts.length < 2) {
			chartEmpty($host, getRes("hist_chart_few"));
			return;
		}
		WORDCHARTS.render($host[0], {
			height: 170,
			yFmt: function (v) { return (v === Math.round(v) ? v : v.toFixed(1)) + "h"; },
			series: [{ kind: "line", pts: pts, r: 2 }]
		});
	}

	// ── the owner-only word-decision audit table (session-word-audit.md) ─────────────────────
	// One SECTION per word, in the order the server sent them: a header naming the word, its
	// direction and the headline verdicts as badges, then every showing and the attempts inside
	// it verbatim, then the full label/M5/garden blocks the shipped code produced — EVERY field
	// of those three blocks visible somewhere ("capture at the decision site, verbatim").
	// Density over prettiness on purpose: this reads like a debugger, not a finish screen,
	// because it exists to let the owner catch the shipped mechanics doing something wrong.

	function auditLevel(sessionId) {
		return {
			title: getRes("audit_title"),
			sessionId: sessionId,   // this level's own identity — see repaintIfShowingAudit
			render: function ($into) { renderAudit($into, sessionId); }
		};
	}

	/** Like repaintIfShowing, but keyed on sessionId rather than on the level's title: every
	 *  audit level carries the SAME title (there is no per-session fact — a timestamp, a word —
	 *  to build a unique one from, unlike openDetail's or openWordLevel's own titles), so two
	 *  audit levels opened one after another would collide on title alone and a slow first fetch
	 *  could land on top of the second. sessionId is set once in auditLevel and never touched
	 *  again — an ordinary property read, not a DOM lookup. */
	function repaintIfShowingAudit(sessionId) {
		if (stack.length && stack[stack.length - 1].sessionId === sessionId) { paintLevel(); }
	}

	function loadAudit(sessionId) {
		// Already in hand (a step back into this same session's audit within one card opening):
		// pushLevel's own paintLevel() just rendered it — nothing left to do, exactly like
		// openWordLevel's identical early return.
		if (auditCache[sessionId]) { return; }
		$.ajax({
			url: "api/session-word-audit",
			type: "GET",
			data: { sessionId: sessionId },
			dataType: "json"
		}).done(function (data) {
			if (!layer) { return; }
			auditCache[sessionId] = data || "failed";
			repaintIfShowingAudit(sessionId);
		}).fail(function (xhr) {
			if (!layer) { return; }
			// 404 = not gated / a foreign session / no rows — "nothing to show", not an error: the
			// level this call just pushed is popped again and a neutral toast says why, rather
			// than leaving an empty table up under a breadcrumb that promised one.
			if (xhr && xhr.status === 404) {
				if (stack.length && stack[stack.length - 1].sessionId === sessionId) { popLevel(); }
				showMessage(getRes("audit_none"));
				return;
			}
			auditCache[sessionId] = "failed";
			repaintIfShowingAudit(sessionId);
		});
	}

	function renderAudit($into, sessionId) {
		var data = auditCache[sessionId];
		if (!data) {
			$into.append(renderState("", getRes("hist_loading")));
			return;
		}
		if (data === "failed") {
			$into.append(renderState("histStateErrClass", getRes("hist_failed")));
			return;
		}
		var words = (data && data.words) || [];
		if (!words.length) {
			$into.append(renderState("", getRes("audit_empty")));
			return;
		}
		// Same split as a session's own word table: the body stops scrolling itself and hands
		// that job to one inner surface, so a long word list cannot carry the breadcrumb header
		// off the top of the card with it. The filter box and the grid line sit ABOVE that
		// surface, the column headings pinned INSIDE its top edge — the scrollbar-width split
		// openWords documents, for the same reason.
		$into.addClass("histBodyDetailClass");
		var $sec = $('<div class="auditSecClass"></div>');
		var $scroll = $('<div class="histWordsClass auditScrollClass"></div>');
		var $legend = auditLineLegend();
		var $empty = renderState("histNoMatchClass", getRes("hist_words_noMatch")).hide();

		var rows = [];
		for (var i = 0; i < words.length; i++) {
			var row = auditWordRow(words[i]);
			rows.push(row);
			$scroll.append(row.el);
		}

		var $count = $('<span class="histWsCountClass"></span>');
		function setCount(shown) {
			$count.text(strFormat(getRes("audit_words_count"), shown === rows.length
				? fmtNum(rows.length)
				: fmtNum(shown) + " " + strFormat(getRes("hist_of"), fmtNum(rows.length))));
		}
		setCount(rows.length);

		// The same box the word lists and the two word tables carry — same classes, same clear ×,
		// same WORDFILTER substitutions, so `ueber` finds *über* here exactly as it does there.
		// No debounce: this filters an array already in hand.
		var $tools = $('<div class="histFilterRowClass"></div>')
			.append(filterBox(function (typed) {
				var hits = 0;
				for (var r = 0; r < rows.length; r++) { hits += rows[r].apply(typed) ? 1 : 0; }
				$empty.toggle(hits === 0);
				$legend.toggle(hits > 0);
				setCount(hits);
			}))
			.append($count)
			.append(auditHeadGrid(words));

		$sec.append($('<div class="histWordsHeadClass"></div>').append($tools))
			.append($scroll.prepend($legend).append($empty));
		$into.append($sec);
		if (window.SCROLL_PAGER) { SCROLL_PAGER.attach($scroll[0]); }
	}

	// ── formatting: null-safe on every field, since label/m5/garden are each an optional whole
	// block and most of their own fields are optional again inside them ──────────────────────

	function auditNum(v) { return (v === null || v === undefined) ? "—" : String(v); }
	function auditMs(v) { return (v === null || v === undefined) ? "—" : (v + " ms"); }
	function auditSim(v) { return (typeof v === "number") ? v.toFixed(3) : "—"; }
	function auditBool(v) {
		return (v === null || v === undefined) ? "—" : getRes(v ? "audit_yes" : "audit_no");
	}
	/** An opaque server string this file does not own a translated vocabulary for (errorClass,
	 *  algoVersion, decisionType, …) — printed verbatim rather than guessed at, which is the
	 *  honest choice for a tool whose entire job is showing exactly what the server said. */
	function auditRaw(v) { return (v === null || v === undefined || v === "") ? "—" : String(v); }
	function auditFlags(v) { return (v && v.length) ? v.join(", ") : "—"; }
	/** Quoted so a leading, trailing or doubled space is visible rather than invisible — the
	 *  monospace face (auditSubmittedClass, CSS) is what keeps it from being collapsed away. */
	function auditSubmitted(v) { return (v === null || v === undefined) ? "—" : ('"' + v + '"'); }
	function auditCompletion(v) {
		return (v === null || v === undefined) ? getRes("audit_censored") : (v + " ms");
	}
	function auditYText(y) {
		return y === true ? getRes("audit_y_pass") : y === false ? getRes("audit_y_fail")
			: getRes("audit_y_void");
	}
	function auditPassFail(s) {
		return auditYText(s === "PASS" ? true : s === "FAIL" ? false : null);
	}

	/** shownTs / dueTs from the audit JSON: defensive about the wire shape (epoch millis, an ISO
	 *  string, or a numeric string all parse) — this is debug output, so a value that truly
	 *  cannot be read prints itself verbatim rather than silently vanishing. */
	function auditTsDate(v) {
		if (v === null || v === undefined || v === "") { return null; }
		var d = new Date(v);
		if (!isNaN(d)) { return d; }
		if (typeof v === "string" && /^\d+$/.test(v)) {
			d = new Date(+v);
			if (!isNaN(d)) { return d; }
		}
		return null;
	}
	/** A due date: day-granularity, like nextCell's own dueMs everywhere else on this card. */
	function auditDue(v) {
		var d = auditTsDate(v);
		return d ? fmtDate(d.getTime()) : auditRaw(v);
	}
	/** A showing's shownTs: time-of-day only — every showing in one session falls on one day. */
	function auditShownAt(v) {
		var d = auditTsDate(v);
		return d ? fmtTime(d.getTime()) : auditRaw(v);
	}

	function auditYBadge(label) {
		if (!label) { return dashCell("auditBadgeClass"); }
		var y = label.y;
		var cls = y === true ? "histVGoodClass" : y === false ? "histVMissClass" : "histVQuietClass";
		return $("<span></span>").addClass("auditBadgeClass")
			.append($('<span class="histVChipClass"></span>').addClass(cls).text(auditYText(y)));
	}

	/** The stage pair the PLANT actually carries. Once learn.srs.growth.mode is ACTIVE the stage is
	 *  GardenGrowthV2's to decide (GardenService: `plant.setStage(v2Active ? v2Plan.stageAfter()
	 *  : plan.stageAfter())`), so the shipped garden block's pair is then the ladder's OPINION, not
	 *  the flower. Under SHADOW it is the other way round. Documents written before the mode was
	 *  captured carry no `mode` at all: those are read as v2-owned (that is what the mode has been
	 *  for every session since growth v2 went ACTIVE), and auditFlowerGroup puts the other engine's
	 *  pair on screen whenever the two disagree — so an ambiguous row shows both rather than
	 *  picking one and sounding certain. */
	function auditStages(a) {
		var g2 = a && a.growthV2;
		if (g2 && (!g2.mode || g2.mode === "ACTIVE")) { return g2; }
		return (a && a.garden) || g2 || null;
	}

	function auditGardenBadge(a, cls) {
		cls = cls || "auditBadgeClass";
		var st = auditStages(a);
		if (!st) { return dashCell(cls); }
		return growthCell(cls, st.stageBefore, st.stageAfter);
	}

	// ── the DECIDING slice (2026-08-24) ───────────────────────────────────────────────────────
	// Everything below answers one question: what actually moved the flower and the next due date?
	// Under P7 flips 1+2 that is ONE composition — VerdictV2 at the user's own notch — read by both
	// the growth pass predicate (learn.srs.growth.pass=verdict-v2) and word_session_label.y, which
	// is what M5 schedules from. So the default view shows the showings that composition READ (the
	// prefix up to its seal — at most `showingsPlanned`, fewer when it sealed early) and the fields
	// those two decisions consume. Nothing is thrown away: the full showings, all ten notches and
	// the verbatim label/M5/garden blocks live one click away under auditDisclosure.

	/** The viewer's own grid, as STORED with the row (see SessionWordAuditService.verdictV2). Older
	 *  documents predate those fields and fall back to the spec's default notch, which is what they
	 *  were composed and rendered under anyway. */
	function auditNotchInfo(v2) {
		if (!v2) { return null; }
		var notch = v2.notch || 5;
		var sealed = v2.notchSealedAt;
		if (sealed === undefined) {
			sealed = v2.sealedAt ? v2.sealedAt[String(notch)] : v2.notch5SealedAt;
		}
		return {
			notch: notch,
			family: v2.family || null,
			band: (typeof v2.band === "number") ? v2.band : null,
			planned: v2.showingsPlanned || null,
			// The rest of the spec §3 row, stored since 2026-08-25. Absent on older documents, and
			// then genuinely unknown rather than assumed: the constants they were composed under
			// are not recoverable from the row, and a diagnostic that guesses is worse than one
			// that prints a dash. peekBudget exists only for family A.
			peekBudget: (typeof v2.peekBudget === "number") ? v2.peekBudget : null,
			timeWeight: (typeof v2.timeWeight === "number") ? v2.timeWeight : null,
			rStar: (typeof v2.rStarTarget === "number") ? v2.rStarTarget : null,
			// A VOID verdict seals nowhere: every showing was read and none of them decided.
			sealed: (sealed === null || sealed === undefined) ? null : sealed
		};
	}

	/** "Notch 5 (B) · 2 planned · band ≥ 0.82 · sealed at 1" — the whole rule in force, in one line,
	 *  because every number under it is only readable against this grid. */
	function auditGridLine(info) {
		if (!info) { return getRes("audit_decision_head"); }
		var bits = [getRes("audit_f_notch") + " " + info.notch + (info.family ? " (" + info.family + ")" : "")];
		if (info.planned) { bits.push(info.planned + " " + getRes("audit_f_showingsPlanned")); }
		if (info.peekBudget !== null) {
			bits.push(getRes("audit_lcol_peeks").toLowerCase() + " " + info.peekBudget);
		}
		if (info.timeWeight !== null) {
			bits.push(getRes("audit_lcol_time").toLowerCase() + " " + auditDec(info.timeWeight));
		}
		if (info.band !== null) { bits.push(getRes("audit_f_band") + " " + auditDec(info.band)); }
		if (info.rStar !== null) {
			bits.push(getRes("audit_lcol_rstar") + " " + auditDec(info.rStar));
		}
		bits.push(info.sealed !== null
			? getRes("audit_v2_sealedAt") + " " + info.sealed
			: getRes("audit_y_void"));
		return bits.join(" · ");
	}

	// ── one line per word (2026-08-25) ────────────────────────────────────────────────────────
	// The default shape of this screen: every word of the session on ONE line, carrying the whole
	// spec §3 grid row it was judged against — planned showings, peek budget, time weight, band,
	// target r* — each as `required → actual`, with the ONE gate that decided a failure flagged
	// and named in plain words at the end of the line. The full deciding slice (the read showings,
	// Verdict / Flower / M5, and its own two disclosures) is one click down, on the same line.
	//
	// Two of those five columns decide nothing TODAY, and the line says so rather than implying a
	// gate that is not there: the time dial is observational (recorded at the reference notch; the
	// composition never reads it) and the per-notch r* is inert while M5 schedules at the flat
	// R_STAR = .90. They are shown dimmed, with the reason on hover.

	/** ".82" — the spec grid's own notation, and directly comparable to the actual beside it. */
	function auditDec(v) {
		if (typeof v !== "number") { return "—"; }
		var s = v.toFixed(2);
		return s.charAt(0) === "0" ? s.slice(1) : s;
	}

	/** The evidence twin of one showing, 1-based like sealedAt / seq. */
	function auditEvidAt(ev, seq) { return (seq && ev[seq - 1]) || null; }

	/** The read showing that came CLOSEST to failing the band — what "actual" means on a word that
	 *  passed: the tightest call of the session, not an average nobody composed against. */
	function auditTightestSeq(ev, read) {
		var best = null, bestSim = null;
		for (var i = 0; i < read && i < ev.length; i++) {
			var sim = ev[i] && ev[i].evidenceSim;
			if (typeof sim !== "number") { continue; }
			if (bestSim === null || sim < bestSim) { bestSim = sim; best = i + 1; }
		}
		return best;
	}

	/** Reveals among the read showings — family A's own gate ("perfect" is its complement). */
	function auditRevealCount(ev, read) {
		var n = 0;
		for (var i = 0; i < read && i < ev.length; i++) {
			if (ev[i] && !ev[i].perfect) { n++; }
		}
		return n;
	}

	/** min(1, latency/allotted) for one showing — the raw half of the time signal, the half that
	 *  is measured rather than dialled. Null when the pair is incomplete. */
	function auditLatencyRatio(showings, ev, seq) {
		var s = (seq && showings[seq - 1]) || {};
		var e = auditEvidAt(ev, seq) || {};
		var lat = s.latencyMs != null ? s.latencyMs : e.latencyMs;
		var allot = e.allottedMs != null ? e.allottedMs : s.allottedMs;
		if (lat == null || !allot) { return null; }
		return Math.min(1, lat / allot);
	}

	/**
	 * One word's whole gate row: five cells, each {target, actual, state, title}, plus the "why"
	 * sentence. `state` is "on" (a gate that ran), "off" (a column nothing reads yet — dimmed) or
	 * "fail" — and EXACTLY ONE cell is ever "fail": the gate that sealed, per family
	 * (VerdictV2.verdictA/B/C). A word with no verdictV2 block at all (a session recorded before
	 * P4, or with learn.srs.closeness.mode OFF) yields dashes rather than invented numbers.
	 */
	function auditGates(a) {
		var v2 = a.verdictV2 || null;
		var info = auditNotchInfo(v2);
		var ev = (v2 && v2.evidence && v2.evidence.showings) || [];
		var showings = a.showings || [];
		var observed = Math.max(ev.length, showings.length);
		var verdict = (v2 && v2.verdicts && info) ? v2.verdicts[String(info.notch)] : null;
		var sealed = info ? info.sealed : null;
		var read = sealed !== null ? Math.min(sealed, observed) : observed;
		var family = info ? info.family : null;
		var failed = verdict === "FAIL";
		// The showing whose numbers the line prints. A FAIL is decided BY one showing — the sealing
		// one — and so is a family-A pass (the first showing to clear ends it). A family B/C pass is
		// the opposite: EVERY read showing had to clear, so the binding one is the tightest, and
		// printing the last would quietly hide the near-miss that made the pass a close-run thing.
		var tightest = auditTightestSeq(ev, read);
		var deciding = (failed || family === "A") && sealed !== null ? sealed : (tightest || sealed);
		var reveals = auditRevealCount(ev, read);
		var sim = (auditEvidAt(ev, deciding) || {}).evidenceSim;
		var ratio = auditLatencyRatio(showings, ev, deciding);
		var m5 = a.m5 || {};
		var rInForce = m5.rStar != null ? Number(m5.rStar) : null;

		var g = { verdict: verdict, sealed: sealed, deciding: deciding, info: info };

		g.showings = {
			target: info && info.planned ? String(info.planned) : "—",
			actual: String(observed),
			state: verdict === "VOID" ? "fail" : "on"
		};
		g.peeks = family === "A"
			? {
				target: info.peekBudget !== null ? String(info.peekBudget) : "—",
				actual: String(reveals),
				state: failed ? "fail" : "on"
			}
			: { target: "—", actual: "", state: "off", title: getRes("audit_gate_off_peeks") };
		g.time = {
			target: auditDec(info && info.timeWeight),
			actual: (info && typeof info.timeWeight === "number" && ratio !== null)
				? auditDec(info.timeWeight * ratio) : "—",
			state: "off",
			title: strFormat(getRes("audit_gate_off_time"), ratio === null ? "—" : auditDec(ratio))
		};
		// Family C never reads the band: R40 makes a reveal a fail outright, so the gate on those
		// notches is "perfect", and printing "≥ .93" there would name a threshold the composition
		// walks straight past. The band it would have used is on hover, which is the whole point of
		// a tool built to catch the mechanics doing something other than the table says.
		g.close = family === "C"
			? {
				target: getRes("audit_gate_perfect"),
				actual: typeof sim === "number" ? auditDec(sim) : "—",
				state: failed ? "fail" : "on",
				title: strFormat(getRes("audit_gate_off_close_c"), auditDec(info && info.band))
			}
			: {
				target: info && info.band !== null ? "≥" + auditDec(info.band) : "—",
				actual: typeof sim === "number" ? auditDec(sim) : "—",
				state: (failed && family === "B") ? "fail" : "on"
			};
		g.rstar = {
			target: auditDec(info && info.rStar),
			actual: m5.rHat != null ? auditDec(Number(m5.rHat)) : "—",
			// r̂ against r* is not a pass/fail gate — it is what SETS the interval — so this cell
			// never flags. It dims when M5 scheduled against a different target than the notch's.
			state: (info && info.rStar !== null && rInForce !== null && rInForce !== info.rStar)
				? "off" : "on",
			title: rInForce !== null
				? strFormat(getRes("audit_gate_off_rstar"), auditDec(rInForce)) : null
		};

		// Which gate sealed is a FAMILY question, and a document written before the family was
		// stored (pre-2026-08-24) cannot answer it: those rows say where the seal fell and stop,
		// rather than naming a gate that may not have been the one — and no cell is flagged either.
		g.why = failed
			? (family === "A" ? strFormat(getRes("audit_why_peek"), sealed)
				: family === "C" ? strFormat(getRes("audit_why_perfect"), sealed)
					: family === "B" ? strFormat(getRes("audit_why_close"), sealed)
						: strFormat(getRes("audit_why_unknown"), sealed))
			: verdict === "VOID"
				? strFormat(getRes("audit_why_void"), observed,
					info && info.planned ? info.planned : "—")
				: verdict === "PASS" ? strFormat(getRes("audit_why_pass"), sealed || read) : "";
		return g;
	}

	function auditGateCell(cell) {
		var $el = $('<span class="auditGateClass"></span>');
		if (!cell) { return $el.addClass("histNilClass").text("—"); }
		$el.append($('<span class="auditGateTgtClass"></span>').text(cell.target));
		if (cell.actual) {
			$el.append($('<span class="auditGateArrowClass" aria-hidden="true"></span>').text("→"))
				.append($('<span class="auditGateActClass"></span>').text(cell.actual));
		}
		if (cell.state === "fail") {
			$el.addClass("auditGateFailClass")
				.append($('<span class="auditGateXClass" aria-hidden="true"></span>').text("✗"));
		}
		if (cell.state === "off") { $el.addClass("auditGateOffClass"); }
		if (cell.title) { $el.attr("title", cell.title); }
		return $el;
	}

	/** The verdict the LINE reports: v2 at the viewer's notch when the document carries one (that
	 *  is the composition both decisions read), else the shipped label's y. */
	function auditLineVerdict(a, gates) {
		if (gates.verdict) {
			return $('<span class="auditLineVClass"></span>').append(auditV2VerdictChip(gates.verdict)
				.removeClass("auditV2NotchChipClass").attr("title", gates.verdict));
		}
		return auditYBadge(a.label).removeClass("auditBadgeClass").addClass("auditLineVClass");
	}

	function auditLineLegend() {
		var $el = $('<div class="auditLineLegendClass plStickyHeadClass"></div>');
		var keys = ["", "hist_wcol_word", "audit_group_verdict", "audit_lcol_showings",
			"audit_lcol_peeks", "audit_lcol_time", "audit_lcol_close", "audit_lcol_rstar",
			"audit_lcol_flower", "audit_lcol_why"];
		for (var i = 0; i < keys.length; i++) {
			$el.append($("<span></span>").text(keys[i] ? getRes(keys[i]) : ""));
		}
		return $el;
	}

	/** The grid line for the SESSION, said once above the table instead of once per row: the notch
	 *  is one live preference read at capture time, so every word of one session normally shares
	 *  it. "Normally" is not "always" — a row composed under a different notch would make this
	 *  header a lie, so a disagreement is stated instead of averaged away. */
	function auditHeadGrid(words) {
		var info = null, mixed = false;
		for (var i = 0; i < words.length; i++) {
			var one = auditNotchInfo(words[i] && words[i].audit && words[i].audit.verdictV2);
			if (!one) { continue; }
			if (!info) { info = one; } else if (one.notch !== info.notch) { mixed = true; }
		}
		if (!info) { return $(); }
		var text = auditGridLine({
			notch: info.notch, family: info.family, planned: info.planned,
			peekBudget: info.peekBudget, timeWeight: info.timeWeight, band: info.band,
			rStar: info.rStar, sealed: null
		// The session line carries no seal — that is a per-word fact, and the trailing "void" the
		// per-word builder appends for a missing one would read as a claim about the session.
		}).replace(" · " + getRes("audit_y_void"), "");
		var $el = $('<span class="auditHeadGridClass"></span>').text(text);
		if (mixed) { $el.append($('<span class="histVMissClass"></span>').text(getRes("audit_grid_mixed"))); }
		return $el;
	}

	function auditWordRow(entry) {
		var a = (entry && entry.audit) || {};
		var term = a.term || {};
		var word = term.term || ("#" + (entry && entry.termId));
		var gates = auditGates(a);

		var $chev = $('<span class="auditChevClass" aria-hidden="true"></span>').html(plIcon("chevron-right"));
		var $text = $('<span class="auditLineTermClass"></span>').text(word)
			.attr("title", fmtDirection({ direction: term.direction }));
		var $line = $('<div class="auditLineClass" role="button" tabindex="0"></div>')
			.append($chev)
			.append($text)
			.append(auditLineVerdict(a, gates))
			.append(auditGateCell(gates.showings))
			.append(auditGateCell(gates.peeks))
			.append(auditGateCell(gates.time))
			.append(auditGateCell(gates.close))
			.append(auditGateCell(gates.rstar))
			.append(auditGardenBadge(a, "auditLineFlowerClass"))
			.append($('<span class="auditWhyClass"></span>').text(gates.why));
		if (gates.verdict === "FAIL") { $line.addClass("auditLineFailClass"); }

		// Built on the FIRST open and kept: a session can carry dozens of words, and the deciding
		// slice is a few hundred nodes each — paying for all of them to render a table nobody has
		// expanded yet is what made the old always-open shape slow to open.
		var $body = $('<div class="auditLineBodyClass"></div>').hide();
		var built = false;
		function toggle() {
			var open = $body.is(":visible");
			if (!open && !built) {
				built = true;
				$body.append(auditWordSection(entry, true));
			}
			$body.toggle(!open);
			$line.toggleClass("auditLineOpenClass", !open);
			$chev.html(plIcon(open ? "chevron-right" : "chevron-down"));
		}
		$line.on("click", toggle).on("keydown", function (e) {
			if (e.which === 13 || e.which === 32) { e.preventDefault(); toggle(); }
		});

		var $el = $('<div class="auditWordRowClass"></div>').append($line).append($body);
		return {
			el: $el,
			apply: function (typed) {
				var re = WORDFILTER.regex(typed, pair.to);
				var hit = !re || re.test(word);
				$el.toggle(hit);
				if (hit) { WORDFILTER.paint($text, word, typed, pair.to); }
				return hit;
			}
		};
	}

	/** One READ showing: the shipped showing row and its v2 evidence twin on ONE line, since the
	 *  pair (perfect, evidenceSim vs band) IS the composition's whole input for that showing. */
	function auditDecisionRow(showing, evid, sealedHere) {
		showing = showing || {};
		evid = evid || {};
		var $row = $('<div class="auditDecideRowClass"></div>')
			.append($("<span></span>").text(auditNum(showing.seq || evid.seq)))
			.append(auditOutcomeChip(showing.outcome))
			.append($("<span></span>").addClass(evid.perfect ? "histVGoodClass" : "histVMissClass")
				.text(getRes(evid.perfect ? "audit_yes" : "audit_no")))
			.append($("<span></span>").text(auditSim(evid.evidenceSim)))
			.append($("<span></span>").addClass("auditSubmittedClass")
				.text(auditSubmitted(evid.bestAttemptText)))
			.append($("<span></span>").text(auditMs(showing.latencyMs || evid.latencyMs)))
			.append($("<span></span>").text(auditMs(evid.allottedMs || showing.allottedMs)))
			.append($("<span></span>").text(auditShownAt(showing.shownTs)));
		if (sealedHere) { $row.addClass("auditSealedRowClass"); }
		return $row;
	}

	function auditDecisionLegend() {
		var $el = $('<div class="auditDecideLegendClass"></div>');
		var keys = ["audit_col_seq", "audit_col_outcome", "audit_v2_evid_col_perfect",
			"audit_v2_evid_col_sim", "audit_acol_submitted", "audit_col_latency",
			"audit_acol_allottedMs", "audit_col_shownTs"];
		for (var i = 0; i < keys.length; i++) {
			$el.append($("<span></span>").text(getRes(keys[i])));
		}
		return $el;
	}

	/** A dimmed one-line door: the trimmed-away detail, kept and reachable, never on screen by
	 *  default. Plain jQuery toggle — no new card LEVEL, because stepping out of the word you are
	 *  reading to see more OF that word is exactly the wrong shape. */
	function auditDisclosure(text, $content) {
		var $wrap = $('<div class="auditMoreClass"></div>');
		var $head = $('<span class="auditMoreHeadClass" role="button" tabindex="0"></span>').text("▸ " + text);
		$content.addClass("auditMoreBodyClass").hide();
		function toggle() {
			var open = $content.is(":visible");
			$content.toggle(!open);
			$head.text((open ? "▸ " : "▾ ") + text);
		}
		$head.on("click", toggle).on("keydown", function (e) {
			if (e.which === 13 || e.which === 32) { e.preventDefault(); toggle(); }
		});
		return $wrap.append($head).append($content);
	}

	/** One label/value pair, the fact strip's own look — reused whole, so "a label/value pair"
	 *  keeps meaning one thing on this card. No tooltip: 28 fields per word is already the point
	 *  of this screen, not a case for 28 more hover explanations. */
	function auditFact(labelKey, value) {
		return $('<div class="histFactClass"></div>')
			.append($('<span class="histFactLblClass"></span>').text(getRes(labelKey)))
			.append($('<span class="histFactValClass"></span>').text(value));
	}

	/** One of the three blocks (label / M5 / garden) as a titled strip of facts — or, per the
	 *  contract, a single dimmed dash when the shipped code produced no block at all for this
	 *  word (never a JS error, and never a grid of nine empty dashes pretending to be data). */
	function auditGroup(titleKey, block, fields) {
		var $group = $('<div class="auditGroupClass"></div>')
			.append($('<div class="histBandHeadClass"></div>').text(getRes(titleKey)));
		if (!block) {
			$group.append(dashCell("auditGroupEmptyClass"));
			return $group;
		}
		var $grid = $('<div class="histFactsClass auditFactsClass"></div>');
		for (var i = 0; i < fields.length; i++) {
			$grid.append(auditFact(fields[i][0], fields[i][1](block)));
		}
		$group.append($grid);
		return $group;
	}

	// Every field of each block, to the letter of the contract — see session-word-audit.md.
	var AUDIT_LABEL_FIELDS = [
		["audit_f_y", function (l) { return auditYText(l.y); }],
		["audit_f_labelFlags", function (l) { return auditFlags(l.labelFlags); }],
		["audit_f_nPres", function (l) { return auditNum(l.nPres); }],
		["audit_f_nMin", function (l) { return auditNum(l.nMin); }],
		["audit_f_criterionMet", function (l) { return auditBool(l.criterionMet); }],
		["audit_f_g", function (l) { return auditNum(l.g); }],
		["audit_f_gFinal", function (l) { return auditNum(l.gFinal); }],
		["audit_f_epsilon", function (l) { return auditNum(l.epsilon); }],
		["audit_f_lStarMs", function (l) { return auditMs(l.lStarMs); }]
	];
	var AUDIT_M5_FIELDS = [
		["audit_f_sBefore", function (m) { return auditNum(m.sBefore); }],
		["audit_f_sAfter", function (m) { return auditNum(m.sAfter); }],
		["audit_f_dAfter", function (m) { return auditNum(m.dAfter); }],
		["audit_f_rHat", function (m) { return auditNum(m.rHat); }],
		["audit_f_rStar", function (m) { return auditNum(m.rStar); }],
		["audit_f_intervalDays", function (m) { return auditNum(m.intervalDays); }],
		["audit_f_floored", function (m) { return auditBool(m.floored); }],
		["audit_f_dueTs", function (m) { return auditDue(m.dueTs); }],
		["audit_f_algoVersion", function (m) { return auditRaw(m.algoVersion); }],
		["audit_f_decisionType", function (m) { return auditRaw(m.decisionType); }]
	];
	// ── the deciding subsets of those same blocks ────────────────────────────────────────────
	// What is NOT here, and why: nPres/nMin (criterionMet is the value the §5.3 update reads —
	// they are its ingredients), gFinal (initState only, i.e. a word's FIRST-ever session), L*
	// (the shipped v1/v3 verdict's own ceiling — under flip 2 it no longer decides y, it only
	// tints the "slow" marker), rStar (the constant 0.90), floored (only ever true on a PROBE,
	// whose lag draw can go under the 1-day floor — Δ* itself is already clamped there) and
	// algoVersion (constant 1 while the M5 engine is the scheduler).
	var AUDIT_VERDICT_FIELDS = [
		["audit_f_y", function (l) { return auditYText(l.y); }],
		["audit_f_labelFlags", function (l) { return auditFlags(l.labelFlags); }],
		["audit_f_criterionMet", function (l) { return auditBool(l.criterionMet); }],
		["audit_f_epsilon", function (l) { return auditNum(l.epsilon); }],
		["audit_f_g", function (l) { return auditNum(l.g); }]
	];
	var AUDIT_M5_DECIDING_FIELDS = [
		["audit_f_sBefore", function (m) { return auditNum(m.sBefore); }],
		["audit_f_sAfter", function (m) { return auditNum(m.sAfter); }],
		["audit_f_rHat", function (m) { return auditNum(m.rHat); }],
		["audit_f_intervalDays", function (m) { return auditNum(m.intervalDays); }],
		["audit_f_dAfter", function (m) { return auditNum(m.dAfter); }],
		["audit_f_decisionType", function (m) { return auditRaw(m.decisionType); }]
	];
	var AUDIT_GARDEN_FIELDS = [
		["audit_f_stageBefore", function (g) { return auditNum(g.stageBefore); }],
		["audit_f_stageAfter", function (g) { return auditNum(g.stageAfter); }],
		["audit_f_result", function (g) { return auditPassFail(g.result); }],
		["audit_f_wasDue", function (g) { return auditBool(g.wasDue); }],
		["audit_f_promoted", function (g) { return auditBool(g.promoted); }],
		["audit_f_planted", function (g) { return auditBool(g.planted); }],
		["audit_f_grew", function (g) { return auditBool(g.grew); }],
		["audit_f_lapses", function (g) { return auditNum(g.lapses); }],
		["audit_f_dueTs", function (g) { return auditDue(g.dueTs); }]
	];

	// The showings' own outcome — the ONE 5-value enum the contract fixes by name, so (unlike
	// errorClass/algoVersion/decisionType above) captions and colour are safe to hardcode for
	// exactly these five. MEET reads quiet rather than good/miss on purpose: it is the "Learn"
	// mode's first-encounter card (learnMeet_* elsewhere in this file's resources), not a graded
	// recall attempt — nothing was measured, the same family as HIST_VERDICT's EARLY_PRACTICE.
	var AUDIT_OUTCOME = {
		PASS:    "histVGoodClass",
		MEET:    "histVQuietClass",
		FAIL:    "histVMissClass",
		TIMEOUT: "histVMissClass",
		REVEAL:  "histVMissClass"
	};

	function auditOutcomeChip(outcome) {
		var cls = AUDIT_OUTCOME[outcome];
		var $chip = $('<span class="histVChipClass"></span>');
		return cls ? $chip.addClass(cls).text(getRes("audit_outcome_" + outcome))
			: $chip.addClass("histVQuietClass").text(auditRaw(outcome));
	}

	function auditAttemptRow(att) {
		att = att || {};
		return $('<div class="auditAttemptRowClass"></div>')
			.append($("<span></span>").text(auditNum(att.attempt)))
			.append($("<span></span>").addClass("auditSubmittedClass")
				.text(auditSubmitted(att.submittedText)))
			.append($("<span></span>").addClass(att.correct ? "histVGoodClass" : "histVMissClass")
				.text(getRes(att.correct ? "audit_yes" : "audit_no")))
			.append($("<span></span>").text(auditSim(att.similarity)))
			.append($("<span></span>").text(auditRaw(att.errorClass)))
			.append($("<span></span>").text(auditMs(att.firstKeyMs)))
			.append($("<span></span>").text(auditCompletion(att.completionMs)))
			.append($("<span></span>").text(auditMs(att.allottedMs)))
			.append($("<span></span>").text(auditNum(att.keystrokes)))
			.append($("<span></span>").text(auditNum(att.backspaces)));
	}

	function auditAttemptsLegend() {
		var $el = $('<div class="auditAttemptLegendClass"></div>');
		var keys = ["audit_acol_attempt", "audit_acol_submitted", "audit_acol_correct",
			"audit_acol_similarity", "audit_acol_errorClass", "audit_acol_firstKeyMs",
			"audit_acol_completionMs", "audit_acol_allottedMs", "audit_acol_keystrokes",
			"audit_acol_backspaces"];
		for (var i = 0; i < keys.length; i++) {
			$el.append($("<span></span>").text(getRes(keys[i])));
		}
		return $el;
	}

	function auditShowingRow(showing) {
		showing = showing || {};
		return $('<div class="auditShowRowClass"></div>')
			.append($("<span></span>").text(auditNum(showing.seq)))
			.append(auditOutcomeChip(showing.outcome))
			.append($("<span></span>").text(auditNum(showing.tier)))
			.append($("<span></span>").text(auditMs(showing.latencyMs)))
			.append($("<span></span>").text(auditShownAt(showing.shownTs)));
	}

	function auditShowingsLegend() {
		var $el = $('<div class="auditShowLegendClass"></div>');
		var keys = ["audit_col_seq", "audit_col_outcome", "audit_col_tier", "audit_col_latency",
			"audit_col_shownTs"];
		for (var i = 0; i < keys.length; i++) {
			$el.append($("<span></span>").text(getRes(keys[i])));
		}
		return $el;
	}

	/** The flower's own block: stage from whichever engine actually wrote it (auditStages), plus the
	 *  growth planner's reasoning when the document carries it and the plant's lapse count. */
	function auditFlowerGroup(a) {
		var st = auditStages(a);
		var g2 = a.growthV2;
		var garden = a.garden;
		var v2Owns = st && st === g2;
		var $group = $('<div class="auditGroupClass"></div>')
			.append($('<div class="histBandHeadClass"></div>')
				.text(getRes("audit_group_flower") + " · " + (v2Owns ? "growth v2" : "M5/ladder")));
		if (!st) {
			return $group.append(dashCell("auditGroupEmptyClass"));
		}
		var $grid = $('<div class="histFactsClass auditFactsClass"></div>');
		$grid.append(auditFact("audit_f_stageBefore", auditNum(st.stageBefore)));
		$grid.append(auditFact("audit_f_stageAfter", auditNum(st.stageAfter)));
		$grid.append(auditFact("audit_f_grew", auditBool(v2Owns
			? (st.stageAfter > st.stageBefore) : garden && garden.grew)));
		if (g2) {
			$grid.append(auditFact("audit_f_bucket", auditRaw(g2.bucket)));
			$grid.append(auditFact("audit_f_nextMarkDays", auditNum(g2.nextMarkDays)));
		}
		if (garden) {
			$grid.append(auditFact("audit_f_lapses", auditNum(garden.lapses)));
		}
		// The OTHER engine's pair, shown only when it disagrees — the case worth seeing (measured
		// on the dev DB 2026-08-24: a v2 2→4 next to a ladder 2→2, and the plant sitting at 4).
		var other = v2Owns ? garden : g2;
		if (other && (other.stageBefore !== st.stageBefore || other.stageAfter !== st.stageAfter)) {
			$grid.append(auditFact("audit_f_otherEngine",
				(v2Owns ? "M5/ladder" : "growth v2") + " " + auditNum(other.stageBefore)
					+ "→" + auditNum(other.stageAfter)));
		}
		return $group.append($grid);
	}

	/** M5's deciding fields plus the ONE date the learner will actually meet the word on again:
	 *  m5.dueTs is the model's exact instant, the garden's is that instant quantized to the start
	 *  of its learning day (GardenScheduler.visibleDue) — the second is the answer to "when does
	 *  this come back", and they can fall on different calendar days. */
	function auditM5Group(a) {
		var $group = auditGroup("audit_group_m5", a.m5, AUDIT_M5_DECIDING_FIELDS);
		if (a.m5) {
			$group.children(".histFactsClass")
				.append(auditFact("audit_f_dueModel", auditDue(a.m5.dueTs)))
				.append(auditFact("audit_f_nextShown", auditDue(a.garden && a.garden.dueTs)));
		}
		return $group;
	}

	/** One showing with its attempts, the verbatim pair — the shape the whole table used to have,
	 *  now the expanded form only. */
	function auditFullShowing(showing) {
		var $out = $("<div></div>").append(auditShowingRow(showing));
		var attempts = (showing && showing.attempts) || [];
		if (attempts.length) {
			var $wrap = $('<div class="auditAttemptsWrapClass"></div>').append(auditAttemptsLegend());
			for (var j = 0; j < attempts.length; j++) {
				$wrap.append(auditAttemptRow(attempts[j]));
			}
			$out.append($wrap);
		}
		return $out;
	}

	/** `noHead` = this section is opening UNDER its own one-line row (auditWordRow), which already
	 *  names the word, its direction and its verdict — repeating them here would put the same three
	 *  facts twice in ten vertical pixels. Every other caller keeps the header. */
	function auditWordSection(entry, noHead) {
		var a = (entry && entry.audit) || {};
		var term = a.term || {};
		var $sec = $('<div class="auditWordSecClass"></div>');

		if (!noHead) {
			var $head = $('<div class="auditWordHeadClass"></div>')
				.append($('<span class="auditWordTermClass"></span>')
					.text(term.term || ("#" + (entry && entry.termId))))
				.append($('<span class="auditWordDirClass"></span>')
					.text(fmtDirection({ direction: term.direction })));
			var $badges = $('<span class="auditBadgesClass"></span>')
				.append(auditYBadge(a.label))
				.append(auditGardenBadge(a))
				.append(verdictCell("auditBadgeClass", a.outcomeToken));
			$head.append($badges);
			$sec.append($head);
		} else {
			// The §9.1 outcome token is the one head fact the line does NOT carry — it is the
			// finish screen's own sentence about the word, and it belongs to this word wherever it
			// is read. It rides in alone.
			$sec.append($('<div class="auditWordHeadClass auditWordHeadBareClass"></div>')
				.append(verdictCell("auditBadgeClass", a.outcomeToken)));
		}

		// ── the showings the verdict READ, and only those ──
		var showings = a.showings || [];
		var evidence = (a.verdictV2 && a.verdictV2.evidence && a.verdictV2.evidence.showings) || [];
		var info = auditNotchInfo(a.verdictV2);
		var read = info && info.sealed !== null ? Math.min(info.sealed, showings.length || evidence.length)
			: (showings.length || evidence.length);

		$sec.append($('<div class="histBandHeadClass auditGridHeadClass"></div>').text(auditGridLine(info)));
		var $decide = $('<div class="auditDecideClass"></div>').append(auditDecisionLegend());
		for (var i = 0; i < read; i++) {
			$decide.append(auditDecisionRow(showings[i], evidence[i], info && i + 1 === info.sealed));
		}
		$sec.append($decide);

		var restCount = Math.max(showings.length, evidence.length) - read;
		if (restCount > 0) {
			var $rest = $("<div></div>").append(auditShowingsLegend());
			for (var k = read; k < showings.length; k++) {
				$rest.append(auditFullShowing(showings[k]));
			}
			$sec.append(auditDisclosure(restCount + " " + getRes("audit_more_showings"), $rest));
		}

		// ── the two decisions those showings drove ──
		$sec.append(auditGroup("audit_group_verdict", a.label, AUDIT_VERDICT_FIELDS));
		$sec.append(auditFlowerGroup(a));
		$sec.append(auditM5Group(a));

		// ── and everything trimmed out of them, one click away ──
		var $more = $("<div></div>");
		if (read > 0) {
			var $readFull = $("<div></div>").append(auditShowingsLegend());
			for (var m = 0; m < read; m++) {
				$readFull.append(auditFullShowing(showings[m]));
			}
			$more.append($readFull);
		}
		$more.append(auditGroup("audit_group_label", a.label, AUDIT_LABEL_FIELDS));
		$more.append(auditGroup("audit_group_m5", a.m5, AUDIT_M5_FIELDS));
		$more.append(auditGroup("audit_group_garden", a.garden, AUDIT_GARDEN_FIELDS));
		var $v2 = auditV2Strip(a.verdictV2);
		if ($v2) { $more.append($v2); }
		$sec.append(auditDisclosure(getRes("audit_more_details"), $more));
		return $sec;
	}

	// ── the P4 shadow-verdict strip (verdict v2 in shadow, garden-srs-implementation-plan.md P4):
	//    the audit table becomes the shadow viewer. Entirely OPTIONAL — omitted whole when the
	//    session's audit document carries no "verdictV2" block (every session before P4 shipped,
	//    or one recorded while learn.srs.closeness.mode was OFF). Reuses the same badge/chip/fact
	//    classes and helper functions the shipped label/m5/garden blocks already use. ────────────

	var AUDIT_V2_VERDICT_CLASS = { PASS: "histVGoodClass", FAIL: "histVMissClass", VOID: "histVQuietClass" };

	function auditV2VerdictChip(v) {
		var cls = AUDIT_V2_VERDICT_CLASS[v] || "histVQuietClass";
		return $('<span class="histVChipClass auditV2NotchChipClass"></span>').addClass(cls)
			.text(v ? v.charAt(0) : "-");
	}

	/** The 10-cell mini-row: one chip per notch, PASS/FAIL/VOID by colour, notch number on hover. */
	function auditV2NotchRow(verdicts) {
		var $row = $('<div class="auditV2NotchRowClass"></div>');
		for (var n = 1; n <= 10; n++) {
			var v = verdicts ? verdicts[String(n)] : null;
			$row.append(auditV2VerdictChip(v).attr("title", "N" + n + ": " + (v || "-")));
		}
		return $row;
	}

	function auditV2EvidRow(s) {
		s = s || {};
		return $('<div class="auditV2EvidRowClass"></div>')
			.append($("<span></span>").text(auditNum(s.seq)))
			.append($("<span></span>").addClass(s.perfect ? "histVGoodClass" : "histVMissClass")
				.text(getRes(s.perfect ? "audit_yes" : "audit_no")))
			.append($("<span></span>").text(auditSim(s.evidenceSim)))
			.append($("<span></span>").addClass(s.vetoFired ? "histVMissClass" : "")
				.text(s.vetoFired ? auditRaw(s.vetoWord) : getRes("audit_no")));
	}

	function auditV2EvidLegend() {
		var $el = $('<div class="auditV2EvidLegendClass"></div>');
		var keys = ["audit_v2_evid_col_seq", "audit_v2_evid_col_perfect", "audit_v2_evid_col_sim",
			"audit_v2_evid_col_veto"];
		for (var i = 0; i < keys.length; i++) {
			$el.append($("<span></span>").text(getRes(keys[i])));
		}
		return $el;
	}

	/** One word's whole v2 (shadow) addendum, or {@code null} when the document carries none. */
	function auditV2Strip(v2) {
		if (!v2) { return null; }
		var $strip = $('<div class="auditV2StripClass"></div>');
		var info = auditNotchInfo(v2);
		var notch = info ? info.notch : 5;
		var $head = $('<div class="histBandHeadClass"></div>').text(getRes("audit_v2_title"))
			.append(auditV2VerdictChip(v2.verdicts ? v2.verdicts[String(notch)] : null))
			.append($('<span class="auditV2SealedClass"></span>')
				.text(getRes("audit_v2_sealedAt") + " " + auditNum(info ? info.sealed : null)));
		$strip.append($head);
		$strip.append(auditV2NotchRow(v2.verdicts));

		var showings = (v2.evidence && v2.evidence.showings) || [];
		if (showings.length) {
			var $evid = $('<div class="auditV2EvidWrapClass"></div>').append(auditV2EvidLegend());
			for (var i = 0; i < showings.length; i++) {
				$evid.append(auditV2EvidRow(showings[i]));
			}
			$strip.append($evid);
		}

		if (v2.guardFlags && v2.guardFlags.length) {
			$strip.append(auditFact("audit_v2_guardFlags", auditFlags(v2.guardFlags)));
		}
		var cut = v2.evidence && v2.evidence.shadowObservation && v2.evidence.shadowObservation.gradedCut;
		if (cut) {
			$strip.append(auditFact("audit_v2_gradedCut",
				cut.closenessBand + " × " + cut.recoveryBand + " → " + auditNum(cut.stabilityCutFactor)
				+ (cut.calibrated ? "" : " (default)")));
		}
		return $strip;
	}

	return {
		open: open,
		openWords: openWords,
		openWord: openWord,
		openAccuracy: openAccuracy,
		openTime: openTime,
		openAudit: openAudit,
		close: close
	};
})();
